import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRememberedPeriod } from './rememberedPeriod';
import * as XLSX from 'xlsx';
import { Link } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { TabBar } from './registerTabs';
import { useApp } from '../appContext';
import { PROJECT_TYPES } from './Configuration';
import {
  MONTHS, CATS, CAT_LABEL, CAT_COLOR, BILLED_LABEL, NEXT_DIM, pct, normalize as normalizeYoy,
  total as yoyTotal, range as yoyRange, capacity as yoyCapacity, groupBy as yoyGroupBy, defaultRange, filterRows,
  sumRows, monthSeries, scopePeople, dims, buildTree, sortBy,
} from './actYoyLogic';

// PROJECT_TYPES as the id/name pairs OptionMultiSelect takes -- the value IS the name here.
const PROJECT_TYPE_OPTIONS = PROJECT_TYPES.map((t) => ({ id: t, name: t }));

// Same umbrella flag as My Time/Resource Planning (RESOURCES_ENABLED) -- these reports are a direct
// extension of that same phase's TimeEntry data, not a separate feature.
const RESOURCES_ENABLED = process.env.REACT_APP_RESOURCES_ENABLED === 'true';

// Filter persistence within a browser tab's session (Jeremy, 2026-09-01): all 4 tabs unmount on tab
// switch and on navigating away, so plain useState was losing every filter pick. Scoped to actual
// filters, not view-only state (expanded rows, sort order).
function usePersistedFilter(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : initialValue;
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* private-mode/quota -- filter just won't persist */ }
  }, [key, value]);
  return [value, setValue];
}

// Fetches a report, cancelling a still-in-flight request if `url` changes again before it resolves
// (an aborted request never flips `loading` back on its own, so there's no flash of "not loading"
// between a cancelled request and the one that replaces it). `url` is null while required filters
// (e.g. no period selected yet) aren't ready; `loading` then just stays true.
//
// Jeremy, 2026-10-02 -- a client-side debounce (collapsing rapid changes within ~400ms) used to sit
// here instead, but it only helped the literal-rapid-click case: building a real filter set (e.g.
// Jan-Sept + 3 teams) is ~12 checkbox clicks that naturally span several seconds, each one past the
// debounce window firing a REAL request against a query heavy enough (5-7s+) that several still
// overlap and 500 -- the same pile-up, just at human clicking speed instead of literal rapid-fire.
// Callers now build `url` from an explicitly-applied filter snapshot (a "Run Report" action), not
// live filter state, so this only ever fires once per deliberate run -- debounce wasn't the fix, not
// auto-fetching on every change was. AbortController stays, for a double-clicked Run.
function useReportFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    setError('');
    const controller = new AbortController();
    apiFetch(url, { signal: controller.signal })
      .then(setData)
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { controller.abort(); };
  }, [url]);

  return { data, loading, error };
}

// Stages a filter set until an explicit "Run Report" click applies it (Jeremy, 2026-10-02 -- see
// useReportFetch's comment: auto-fetching on every filter change, even debounced, was the actual
// bug). `current` is a fresh object each render built from the live filter state; `applied` only
// changes on `run()`, or once automatically the first time `ready` turns true so the report isn't
// empty on initial load. `isDirty` lets a tab flag that the staged filters no longer match what's
// shown, without forcing a re-run.
function useAppliedFilters(current, ready) {
  const [applied, setApplied] = useState(null);
  // Not useCallback: `current` is a fresh object literal every render, so there's nothing stable to
  // memoize against -- a plain function says that honestly instead of implying `run` is referentially
  // stable when it never is.
  const run = () => setApplied(current);
  useEffect(() => {
    if (ready && applied === null) setApplied(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
  const isDirty = applied !== null && JSON.stringify(applied) !== JSON.stringify(current);
  return { applied, isDirty, run };
}

// The button + "filters changed" banner both tabs render identically around their staged filters.
// `.notice-warn` is the repo's existing staged-state-callout convention (DataConversion.js,
// BPATracker.js) -- reused here instead of one-off inline styles. The duration hint exists because
// this fetches a genuinely multi-second query (Jeremy, 2026-10-02 -- see useReportFetch's comment);
// an unexplained multi-second "Loading…" otherwise reads as hung.
function RunReportBar({ isDirty, onRun, disabled }) {
  return (
    <>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onRun}
        disabled={disabled}
        title={isDirty ? 'Filters have changed since the last run' : undefined}
      >
        {isDirty ? 'Run Report *' : 'Run Report'}
      </button>
      {isDirty && (
        <div className="notice notice-warn" style={{ width: '100%' }}>
          Filters have changed — click <strong>Run Report</strong> to refresh these results.
        </div>
      )}
    </>
  );
}

// Compliance status colors -- mirrors the 5-tier legend from the PowerBI report this replaces.
// No --orange/--blue CSS var exists yet (App.css only defines --red/--yellow/--green), so "In
// Progress" and "Submitted" use one-off hex/var values here rather than inventing new global vars
// for a single page.
const STATUS_STYLES = {
  'Not Started':     { background: 'rgba(239,65,54,0.15)',  color: 'var(--red)' },
  'In Progress':      { background: 'rgba(242,153,74,0.18)', color: 'var(--warn-text)' },
  'Submitted':        { background: 'rgba(47,147,177,0.15)', color: 'var(--primary-light)' },
  'Manager Approved': { background: 'rgba(251,176,64,0.18)', color: 'var(--warn-text)' },
  'Approved':         { background: 'rgba(0,124,100,0.15)',  color: 'var(--green)' },
};

// Tiny +/- expand-all / collapse-all affordance in each panel card's title row -- deliberately not
// a .btn, so it stays on one line with the title (Jeremy, 2026-08-07).
const EXPANDER_BTN = {
  width: 18, height: 18, lineHeight: '16px', padding: 0, fontSize: 13, fontWeight: 700,
  border: '1px solid var(--border)', borderRadius: 3, background: 'var(--surface)',
  color: 'var(--text-mid)', cursor: 'pointer',
};

function safeFileSegment(value) {
  return (value || '').replace(/[\\/:*?"<>|]/g, '-').trim();
}

function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div
      style={{
        padding: '12px 16px', borderRadius: 8, marginBottom: 20,
        background: 'rgba(239,65,54,0.08)', border: '1px solid rgba(239,65,54,0.3)',
        fontSize: 13, color: 'var(--red)',
      }}
    >
      ⚠ {error}
    </div>
  );
}

// Sort indicator + click-to-sort header -- same pattern as Configuration.js's People tab (duplicated
// rather than shared, same "no shared component layer" convention as this app's other small UI
// helpers). Going forward, every grid on this page gets sortable column headers by default
// (Jeremy, 2026-08-06) -- Accounting's Project-grouped rowSpan layout is the one deliberate
// exception, since it mirrors Finance's own export format and sorting would break that grouping.
function SortTh({ label, sortKey, sort, onSort, style, title }) {
  const active = sort.key === sortKey;
  return (
    <th style={{ textAlign: 'left', cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => onSort(sortKey)} title={title}>
      {label} <span style={{ opacity: active ? 1 : 0.25 }}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
    </th>
  );
}

// Anyone who shows up as someone else's manager -- backs the Manager filter dropdowns without a
// dedicated endpoint. Filters by name (matches how ReportsService returns manager/team as display
// strings, not ids); fine for a small org where names are unique in practice.
export function managersOf(users) {
  const managerIds = new Set(users.map((u) => u.managerId).filter(Boolean));
  return users.filter((u) => managerIds.has(u.id));
}

// "FY26-M07" -> "FY26" -- the fiscal-year prefix every periodName/periodMonth shares.
export function yearOf(periodMonth) {
  return (periodMonth || '').split('-')[0];
}

// Distinct fiscal years present in a periods list, in the order they already come in (chronological
// -- ReportsService.listPeriods orders by startDate).
export function yearsOf(periods) {
  return [...new Set(periods.map(yearOf))];
}

// Nested/cascading filter options (Jeremy, 2026-08-06): a Team filter narrows which managers and
// people can even be picked next, and a Manager filter (already within whichever team is picked)
// narrows people further to that manager's direct reports. Shared by all three filtered tabs, which
// are all multi-team as of 2026-08-11; an empty list means all teams.
export function usersInTeams(users, teamIds) {
  return teamIds.length > 0 ? users.filter((u) => teamIds.includes(u.teamId)) : users;
}
// Takes a LIST of managers (2026-10-07, when Manager went multi-select): a person stays in the pool
// if they report to any one of them. An empty list is no filter at all.
export function directReportsOf(users, managerIds) {
  const managers = managerIds?.filter(Boolean) ?? [];
  return managers.length > 0 ? users.filter((u) => managers.includes(u.managerId)) : users;
}

// Narrows every period selector on the page to one fiscal year at a time (Jeremy, 2026-08-06) --
// shared at the top level, defaulting to whichever year contains today, so the period dropdowns
// below don't have to list every fiscal year's weeks/months at once.
function YearSelector({ years, year, onChange }) {
  if (years.length <= 1) return null;
  return (
    <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
      <label className="form-label">Year</label>
      <select className="form-select" value={year} onChange={(e) => onChange(e.target.value)}>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

// Shared by Accounting/Compliance -- lets switching between them keep the selected period instead
// of each re-defaulting independently. Hours by Category has its own multi-select instead (see
// PeriodMultiSelect below), since it's the one report allowed to combine several fiscal periods.
function PeriodSelector({ periods, periodMonth, onChange }) {
  return (
    <div className="form-group" style={{ marginBottom: 0, width: 180 }}>
      <label className="form-label">Fiscal period</label>
      <select className="form-select" value={periodMonth} onChange={(e) => onChange(e.target.value)}>
        {periods.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
  );
}

// ---------- Hours by Billable Category ----------

function toggled(set, key) {
  const next = new Set(set);
  if (next.has(key)) next.delete(key); else next.add(key);
  return next;
}
function withKeys(set, keys, on) {
  const next = new Set(set);
  for (const key of keys) if (on) next.add(key); else next.delete(key);
  return next;
}

// Collapsed to a dropdown (Jeremy, 2026-08-07) so the filter row stays one line tall; the open
// panel keeps the 6-per-row grid so a fiscal year's 12 periods read as two even rows.
function PeriodMultiSelect({ periods, selected, onChange }) {
  const [open, setOpen] = useState(false);
  function toggle(period) {
    onChange(selected.includes(period) ? selected.filter((p) => p !== period) : [...selected, period]);
  }
  const summary = selected.length === 0 ? 'None selected'
    : selected.length === 1 ? selected[0]
    : `${selected.length} periods`;

  return (
    <div className="form-group" style={{ marginBottom: 0, width: 180, position: 'relative' }}>
      <label className="form-label">Fiscal period(s)</label>
      <button
        type="button"
        className="form-select"
        onClick={() => setOpen((v) => !v)}
        style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}
      >
        {summary} ▾
      </button>
      {open && (
        <>
          {/* Click-away backdrop -- cheaper than a document listener for a dropdown this small. */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 11, marginTop: 4, padding: 10,
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, width: 480 }}>
              {periods.map((p) => (
                <label
                  key={p}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 12, padding: '4px 8px',
                    border: '1px solid var(--border)', borderRadius: 12, cursor: 'pointer',
                    background: selected.includes(p) ? 'var(--primary-dark)' : 'var(--surface)',
                    color: selected.includes(p) ? 'var(--white)' : 'var(--text-mid)',
                  }}
                >
                  <input type="checkbox" checked={selected.includes(p)} onChange={() => toggle(p)} style={{ display: 'none' }} />
                  {p}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => onChange([])}>Clear</button>
              <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => onChange(periods)}>Select all</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Same dropdown-of-checkboxes shape as PeriodMultiSelect, over {id, name} options and one per line
// (team names are words, not FY26-Mxx codes, so a chip grid doesn't fit them).
// One checklist dropdown for every id/name filter on this page -- Team, Project, Project Type,
// Manager, Person. Same shape as PeriodMultiSelect above, but a plain vertical list rather than a
// chip grid, since these lists are long and alphabetical.
function OptionMultiSelect({ label, options, selected, onChange, allLabel, noun, width = 150 }) {
  const [open, setOpen] = useState(false);
  function toggle(id) {
    onChange(selected.includes(id) ? selected.filter((t) => t !== id) : [...selected, id]);
  }
  const summary = selected.length === 0 ? allLabel
    : selected.length === 1 ? options.find((o) => o.id === selected[0])?.name || `1 ${noun}`
    : `${selected.length} ${noun}`;

  return (
    <div className="form-group" style={{ marginBottom: 0, width, position: 'relative' }}>
      <label className="form-label">{label}</label>
      <button type="button" className="form-select" onClick={() => setOpen((v) => !v)} style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}>
        {summary} ▾
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 11, marginTop: 4, padding: 10,
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 200, maxHeight: 280, overflowY: 'auto',
            }}
          >
            {options.map((o) => (
              <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
                {o.name}
              </label>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => onChange([])}>Clear</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Week filter (Jeremy, 2026-09-16) -- narrows Hours by Category inside whichever fiscal periods are
// already picked, so it's empty-means-all rather than a second period selector. Same
// dropdown-of-checkboxes shape as the two above; weeks are FY26-Wnn codes, so the chip grid fits.
function WeekMultiSelect({ weeks, selected, onChange }) {
  const [open, setOpen] = useState(false);
  function toggle(name) {
    onChange(selected.includes(name) ? selected.filter((w) => w !== name) : [...selected, name]);
  }
  const summary = selected.length === 0 ? 'All weeks'
    : selected.length === 1 ? selected[0]
    : `${selected.length} weeks`;

  return (
    <div className="form-group" style={{ marginBottom: 0, width: 150, position: 'relative' }}>
      <label className="form-label">Week(s)</label>
      <button type="button" className="form-select" onClick={() => setOpen((v) => !v)} style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }} disabled={weeks.length === 0}>
        {summary} ▾
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 11, marginTop: 4, padding: 10,
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 230, maxHeight: 300, overflowY: 'auto',
            }}
          >
            {weeks.map((w) => (
              <label key={w.periodName} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(w.periodName)} onChange={() => toggle(w.periodName)} />
                <span>{w.periodName}</span>
                <span style={{ color: 'var(--text-light)', fontSize: 11, marginLeft: 'auto' }}>
                  {new Date(w.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </label>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => onChange([])}>Clear</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function HoursSummaryTab({ periods, defaultPeriodMonth, teams, users, years, year, onYearChange }) {
  const [selectedPeriods, setSelectedPeriods] = useRememberedPeriod('timeReports.hoursSummary.selectedPeriods', []);
  const [selectedWeeks, setSelectedWeeks] = useRememberedPeriod('timeReports.hoursSummary.selectedWeeks', []);
  const [allWeeks, setAllWeeks] = useState([]);
  const [weeksLoaded, setWeeksLoaded] = useState(false);
  const [teamIds, setTeamIds] = usePersistedFilter('timeReports.hoursSummary.teamIds', []);
  const [managerIds, setManagerIds] = usePersistedFilter('timeReports.hoursSummary.managerIds', []);
  const [userIds, setUserIds] = usePersistedFilter('timeReports.hoursSummary.userIds', []);
  // Which "panel|row" keys are expanded to show their teams (Jeremy, 2026-08-07) -- keyed by panel
  // since row names repeat across panels (e.g. the same project shows up under both Billable Project
  // Work and Core Product Work). "panel|row|teamId" is the second level, down to people.
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  // Per-panel depth the +/- buttons are currently at, so they step one level at a time (Jeremy,
  // 2026-08-11) rather than jumping straight to fully-open. Clicking rows directly doesn't update it
  // -- the next +/- press still resolves to a whole level, which is what makes the buttons
  // predictable regardless of what was opened by hand.
  const [panelLevels, setPanelLevels] = useState({});

  function toggleRow(key) {
    setExpandedRows((prev) => toggled(prev, key));
  }
  function toggleTeam(key) {
    setExpandedTeams((prev) => toggled(prev, key));
  }

  function stepPanel(panel, delta) {
    const level = Math.max(0, Math.min(2, (panelLevels[panel.panel] ?? 0) + delta));
    setPanelLevels((prev) => ({ ...prev, [panel.panel]: level }));
    const rowKeys = panel.rows.map((row) => `${panel.panel}|${row.name}`);
    const teamKeys = panel.rows.flatMap((row) => row.teams.map((team) => `${panel.panel}|${row.name}|${team.teamId}`));
    setExpandedRows((prev) => withKeys(prev, rowKeys, level >= 1));
    setExpandedTeams((prev) => withKeys(prev, teamKeys, level >= 2));
  }

  // Defaults to the current period once it's known, same as the other two tabs -- but as a
  // multi-select seed, not a fixed value, so the user can add more without losing it.
  useEffect(() => {
    if (defaultPeriodMonth && selectedPeriods.length === 0) setSelectedPeriods([defaultPeriodMonth]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPeriodMonth]);

  // `periods` is already narrowed to one fiscal year by the page-level Year selector -- when the
  // year changes, drop any selection that's no longer in the list rather than silently querying a
  // period the dropdown no longer shows as selectable.
  useEffect(() => {
    setSelectedPeriods((prev) => {
      const kept = prev.filter((p) => periods.includes(p));
      if (kept.length > 0) return kept;
      return defaultPeriodMonth && periods.includes(defaultPeriodMonth) ? [defaultPeriodMonth] : [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods]);

  // Every fiscal week once; the dropdown shows only those inside the selected periods.
  useEffect(() => {
    apiFetch('/dashboard/reports/weeks').then(setAllWeeks).catch(() => setAllWeeks([])).finally(() => setWeeksLoaded(true));
  }, []);

  const weekOptions = useMemo(
    () => allWeeks.filter((w) => selectedPeriods.includes(w.periodMonth)),
    [allWeeks, selectedPeriods],
  );

  // Drop week picks that the current period selection no longer contains -- same reasoning as the
  // period/year effect above: never query on a filter the dropdown doesn't show as selectable.
  useEffect(() => {
    // Not until there's something real to prune against: on mount weekOptions is [] (periods not
    // seeded, /weeks not back), and pruning then would wipe a restored selection -- and write the
    // empty list straight back to sessionStorage, so the filter could never survive a tab switch.
    if (allWeeks.length === 0 || selectedPeriods.length === 0) return;
    setSelectedWeeks((prev) => {
      const kept = prev.filter((w) => weekOptions.some((o) => o.periodName === w));
      // Same array back when nothing was dropped -- a new [] every time would re-fire the fetch
      // below (it's a dependency), which flips the whole report back to Loading… mid-interaction.
      return kept.length === prev.length ? prev : kept;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOptions, allWeeks, selectedPeriods]);

  const currentFilters = { selectedPeriods, selectedWeeks, teamIds, managerIds, userIds };
  // Waits on weeksLoaded too, not just selectedPeriods (Jeremy, 2026-10-02): the one-shot auto-run
  // snapshots whatever `selectedWeeks` sessionStorage restored, before the week-pruning effect above
  // has had a chance to drop a pick that's no longer valid for the restored period -- without this,
  // the first request on a remount could go out with a stale periodName and immediately show "Filters
  // have changed" with no action taken. `weeksLoaded`, not `allWeeks.length > 0`: an org with zero
  // FiscalWeek rows in range is still "loaded," just empty -- it must not block the auto-run forever.
  const { applied, isDirty, run: runReport } = useAppliedFilters(currentFilters, selectedPeriods.length > 0 && weeksLoaded);

  const hoursSummaryUrl = useMemo(() => {
    if (!applied || applied.selectedPeriods.length === 0) return null;
    const params = new URLSearchParams({ periodMonth: applied.selectedPeriods.join(',') });
    if (applied.selectedWeeks.length > 0) params.set('periodName', applied.selectedWeeks.join(','));
    if (applied.teamIds.length > 0) params.set('teamId', applied.teamIds.join(','));
    if (applied.managerIds.length > 0) params.set('managerId', applied.managerIds.join(','));
    if (applied.userIds.length > 0) params.set('userId', applied.userIds.join(','));
    return `/dashboard/reports/hours-summary?${params}`;
  }, [applied]);
  const { data, loading, error } = useReportFetch(hoursSummaryUrl);

  // Cascading options (see usersInTeams/directReportsOf's comment): Team narrows both the Manager and
  // Person pools; Manager narrows the (already team-narrowed) Person pool down to direct reports.
  const teamScopedUsers = useMemo(() => usersInTeams(users, teamIds), [users, teamIds]);
  const managers = useMemo(() => managersOf(teamScopedUsers), [teamScopedUsers]);
  const personOptions = useMemo(() => directReportsOf(teamScopedUsers, managerIds), [teamScopedUsers, managerIds]);

  function onTeamChange(value) {
    setTeamIds(value);
    setManagerIds([]);
    setUserIds([]);
  }
  function onManagerChange(value) {
    setManagerIds(value);
    setUserIds([]);
  }

  // Total is every hour in the filtered report, time off included -- so it won't tie to the three
  // category tiles below it, which is why the time-off share is called out in its sub-label.
  const totals = useMemo(() => {
    const all = (data?.panels || []).reduce((sum, p) => sum + p.total, 0);
    const timeOff = (data?.panels || []).find((p) => p.panel === 'Time Off')?.total ?? 0;
    return { all: Math.round(all * 100) / 100, timeOff: Math.round(timeOff * 100) / 100 };
  }, [data]);

  // Every drill-down level, flattened -- `data` is already the filtered response, so the export
  // honors the filters whether or not the rows happen to be expanded on screen.
  function exportXlsx() {
    if (!data) return;
    const sheetRows = [];
    for (const panel of data.panels) {
      for (const row of panel.rows) {
        sheetRows.push({ Panel: panel.panel, Row: row.name, Team: '', Person: '', Level: 'Row', Hours: row.hours });
        for (const team of row.teams) {
          sheetRows.push({ Panel: panel.panel, Row: row.name, Team: team.teamName, Person: '', Level: 'Team', Hours: team.hours });
          for (const person of team.people) {
            sheetRows.push({ Panel: panel.panel, Row: row.name, Team: team.teamName, Person: person.name, Level: 'Person', Hours: person.hours });
          }
        }
      }
      sheetRows.push({ Panel: panel.panel, Row: 'Total', Team: '', Person: '', Level: 'Panel total', Hours: panel.total });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetRows), 'Hours by Category');
    // Named from `applied`, not the live (possibly staged-but-not-run) selection -- `data` itself is
    // always `applied`'s result, so the filename must match what's actually in the file (2026-10-02).
    XLSX.writeFile(wb, `Hours by Category ${safeFileSegment(applied.selectedPeriods.join(' '))}.xlsx`);
  }

  return (
    <div>
      <ErrorBanner error={error} />
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <YearSelector years={years} year={year} onChange={onYearChange} />
        <PeriodMultiSelect periods={periods} selected={selectedPeriods} onChange={setSelectedPeriods} />
        <WeekMultiSelect weeks={weekOptions} selected={selectedWeeks} onChange={setSelectedWeeks} />
        <OptionMultiSelect label="Team(s)" options={teams} selected={teamIds} onChange={onTeamChange} allLabel="All teams" noun="teams" />
        <OptionMultiSelect label="Manager(s)" options={managers} selected={managerIds} onChange={onManagerChange} allLabel="All managers" noun="managers" width={160} />
        <OptionMultiSelect label="Person(s)" options={personOptions} selected={userIds} onChange={setUserIds} allLabel="All people" noun="people" width={160} />
        <RunReportBar isDirty={isDirty} onRun={runReport} disabled={selectedPeriods.length === 0} />
        {data && (
          <button className="btn btn-outline" style={{ marginLeft: 'auto' }} onClick={exportXlsx}>Export to Excel</button>
        )}
      </div>

      {loading ? (
        <div className="card">Loading… this can take several seconds for a wide date range or several teams.</div>
      ) : (
        <>
        {data && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <MetricTile label="Total" value={totals.all} sub={`hours logged${totals.timeOff ? ` · incl. ${num(totals.timeOff)} time off` : ''}`} />
            <MetricTile label="Billable" value={data.categoryTotals?.billable ?? 0} sub={<PanelBreakdown panels={data.categoryPanels?.billable} />} />
            <MetricTile label="Non-Billable Productive" value={data.categoryTotals?.nonBillableProductive ?? 0} sub={<PanelBreakdown panels={data.categoryPanels?.nonBillableProductive} />} />
            <MetricTile label="Non-Billable" value={data.categoryTotals?.nonBillable ?? 0} sub={<PanelBreakdown panels={data.categoryPanels?.nonBillable} />} />
          </div>
        )}
        {data && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <MetricTile
              basis={340}
              label="Billable Utilization"
              value={data.utilization?.billablePct == null ? '—' : `${data.utilization.billablePct}%`}
              tone="var(--green)"
              sub={<UtilizationFormula util={data.utilization} hours={data.utilization?.billableHours} numerator="Billable hours" />}
            />
            <MetricTile
              basis={340}
              label="Productive Utilization"
              value={data.utilization?.productivePct == null ? '—' : `${data.utilization.productivePct}%`}
              sub={<UtilizationFormula util={data.utilization} hours={data.utilization?.productiveHours} numerator="Billable + Non-Billable Productive hours" />}
            />
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {(data?.panels || []).map((panel) => (
            <div key={panel.panel} className="card" style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div className="card-title" style={{ marginBottom: 0 }}>{panel.panel}</div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button type="button" style={EXPANDER_BTN} title="Expand one level" onClick={() => stepPanel(panel, 1)}>+</button>
                  <button type="button" style={EXPANDER_BTN} title="Collapse one level" onClick={() => stepPanel(panel, -1)}>−</button>
                </div>
              </div>
              <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                  {panel.rows.map((row) => {
                    const key = `${panel.panel}|${row.name}`;
                    const isOpen = expandedRows.has(key);
                    return (
                      <React.Fragment key={row.name}>
                        <tr onClick={() => toggleRow(key)} style={{ cursor: 'pointer' }}>
                          <td style={{ padding: '4px 0', color: 'var(--text-mid)' }}>
                            <span style={{ display: 'inline-block', width: 14, opacity: 0.5 }}>{isOpen ? '▾' : '▸'}</span>
                            {row.name}
                          </td>
                          <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 600 }}>{num(row.hours)}</td>
                        </tr>
                        {isOpen && row.teams.map((team) => {
                          const teamKey = `${key}|${team.teamId}`;
                          const teamOpen = expandedTeams.has(teamKey);
                          return (
                            <React.Fragment key={teamKey}>
                              <tr onClick={() => toggleTeam(teamKey)} style={{ cursor: 'pointer' }}>
                                <td style={{ padding: '3px 0 3px 20px', color: 'var(--text-mid)', fontSize: 12 }}>
                                  <span style={{ display: 'inline-block', width: 12, opacity: 0.5 }}>{teamOpen ? '▾' : '▸'}</span>
                                  {team.teamName}
                                </td>
                                <td style={{ padding: '3px 0', textAlign: 'right', fontSize: 12, fontWeight: 600 }}>{num(team.hours)}</td>
                              </tr>
                              {teamOpen && team.people.map((person) => (
                                <tr key={person.name}>
                                  <td style={{ padding: '2px 0 2px 46px', color: 'var(--text-mid)', fontSize: 12 }}>{person.name}</td>
                                  <td style={{ padding: '2px 0', textAlign: 'right', fontSize: 12, color: 'var(--text-mid)' }}>{num(person.hours)}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {/* Grid stretches every card in a row to the tallest one's height (no alignItems override
                  on the grid below) -- this spacer pushes Total to the bottom of that stretched card
                  rather than right under the last row (Jeremy, 2026-08-31). */}
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 6, fontSize: 13, fontWeight: 700 }}>
                <span>Total</span>
                <span>{num(panel.total)}</span>
              </div>
            </div>
          ))}
          {/* Zero-total panels are dropped server-side (2026-08-31), so an empty list IS "nothing". */}
          {data && data.panels.length === 0 && (
            <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>No hours logged for this selection.</div>
          )}
        </div>
        </>
      )}
    </div>
  );
}

// ---------- FCST vs Actuals ----------

// The frozen name column's pinned width -- shared by the header and every body cell so they can't
// drift apart (same rule as BPATracker's freezePanes).
const FROZEN_NAME_COL = { width: 240, minWidth: 240, maxWidth: 240 };

// Signed, so the sign carries the meaning the color repeats: positive = burned more than planned.
export function variance(cell) {
  return Math.round((cell.act - cell.fcst + Number.EPSILON) * 100) / 100;
}
export function baselineVariance(cell) {
  return Math.round((cell.act - cell.bl + Number.EPSILON) * 100) / 100;
}

// Achievement = how much of the plan was actually delivered; Accuracy = how close the plan was in
// either direction (a 20% over-run and a 20% under-run are equally wrong). Both null when there was
// no plan to measure against -- an unplanned project is not 0% accurate, it's unmeasured.
// First-pass definitions (Jeremy to confirm) -- the PowerBI report's own DAX isn't reproduced here.
export function achievementPct(act, planned) {
  if (!planned) return null;
  return Math.round((act / planned) * 1000) / 10;
}
export function accuracyPct(act, planned) {
  if (!planned) return null;
  return Math.round(Math.max(0, 1 - Math.abs(act - planned) / planned) * 1000) / 10;
}

// Green over forecast, red under (Jeremy, 2026-08-09) -- hours logged above plan is the good news
// here; the risk this report is watching for is work that was forecast and never delivered.
function varianceStyle(value) {
  if (!value) return { color: 'var(--text-light)' };
  return { color: value > 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 };
}

function fmt(n) {
  return n ? num(n) : '';
}
function fmtSigned(n) {
  return n ? (n > 0 ? `+${num(n)}` : `${num(n)}`) : '';
}

// How a row's kind reads in the Excel export's Level column.
const ROW_LEVEL_LABELS = { person: 'Person', team: 'Team plan', rnd: 'R&D' };

export function fcstSortValue(row, key) {
  switch (key) {
    case 'fcst': return row.total.fcst;
    case 'act': return row.total.act;
    case 'bl': return row.total.bl;
    case 'var': return variance(row.total);
    case 'blvar': return baselineVariance(row.total);
    default: return (row.projectName || '').toLowerCase();
  }
}

// Which cards feed a category tile (Jeremy, 2026-09-16). Worth showing because the mapping isn't
// one card to one tile: Travel by Customer splits across two, so "why is Non-Billable Productive
// bigger than Core + Market?" is otherwise unanswerable from the cards alone.
function PanelBreakdown({ panels }) {
  if (!panels?.length) return 'hours';
  return (
    <span>
      {panels.map((p, i) => (
        <span key={p.panel}>
          {i > 0 && ' · '}
          {p.panel} {num(p.hours)}
        </span>
      ))}
    </span>
  );
}

// The formula, spelled out, because this denominator is capacity -- not "hours you logged" the way
// My Time's recap reads (Jeremy, 2026-09-16). Anyone comparing the two numbers needs to see why
// they differ without going to the docs.
function UtilizationFormula({ util, hours, numerator }) {
  if (!util) return null;
  const people = util.userCount === 1 ? '1 person' : `${util.userCount} people`;
  return (
    <span>
      {numerator} {num(hours ?? 0)} ÷ capacity {num(util.capacityHours || 0)}
      {' '}({people}{util.prorated ? ', pro-rated to the weeks selected' : ''})
      {util.usersWithoutCapacity > 0 && (
        <span style={{ color: 'var(--text-light)' }}>
          {' '}· {util.usersWithoutCapacity} excluded, no capacity set
        </span>
      )}
    </span>
  );
}

// Hours are read like money on this page -- five figures are common, so they get thousands
// separators (Judy, 2026-10-08). Whole numbers keep no trailing ".00", and anything that isn't a
// finite number passes straight through: several tiles render an em dash or an already-formatted
// percentage.
export function num(v) {
  return typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString('en-US', { maximumFractionDigits: 2 })
    : v;
}

// Zero reads as blank in the grids, so the check has to come before formatting ('0' is truthy).
export function numBlank(v) {
  return v ? num(v) : '';
}

function MetricTile({ label, value, sub, tone, basis = 150 }) {
  return (
    <div className="card" style={{ flex: `1 1 ${basis}px`, minWidth: basis, padding: '12px 16px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-light)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: tone || 'var(--heading)', fontVariantNumeric: 'tabular-nums' }}>{num(value)}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-light)' }}>{sub}</div>}
    </div>
  );
}

// Plan vs actual as two stacked bars on a shared scale -- the fastest read of "did we burn more or
// less than we said", without pulling in a charting library this app doesn't have.
function PlanActualBar({ planned, act, max }) {
  if (!max) return null;
  const bar = (value, color) => (
    <div style={{ height: 5, borderRadius: 3, background: color, width: `${Math.min(100, (value / max) * 100)}%`, minWidth: value ? 2 : 0 }} />
  );
  return (
    <div style={{ display: 'grid', gap: 2, minWidth: 70 }} title={`Forecast ${planned} / Actual ${act}`}>
      {bar(planned, 'var(--primary-light)')}
      {bar(act, 'var(--green)')}
    </div>
  );
}

function FcstVsActualsTab({ periods, defaultPeriodMonth, teams, users, projects, baselines, years, year, onYearChange }) {
  const [selectedPeriods, setSelectedPeriods] = useRememberedPeriod('timeReports.fcst.selectedPeriods', []);
  const [projectIds, setProjectIds] = usePersistedFilter('timeReports.fcst.projectIds', []);
  const [projectTypes, setProjectTypes] = usePersistedFilter('timeReports.fcst.projectTypes', []);
  const [teamIds, setTeamIds] = usePersistedFilter('timeReports.fcst.teamIds', []);
  const [managerIds, setManagerIds] = usePersistedFilter('timeReports.fcst.managerIds', []);
  const [userIds, setUserIds] = usePersistedFilter('timeReports.fcst.userIds', []);
  const [baselineId, setBaselineId] = usePersistedFilter('timeReports.fcst.baselineId', '');
  // Off by default (Jeremy, 2026-10-07): this report is PS delivery unless someone asks for the
  // whole picture. R&D rows come from RndMonthlyEntry, not from any PS forecast or timesheet.
  const [includeRnd, setIncludeRnd] = usePersistedFilter('timeReports.fcst.includeRnd', false);
  const [expanded, setExpanded] = useState(new Set());
  // Second expand level (project -> team -> people): keyed `projectId::teamId`, collapsed by default
  // so opening a project shows its teams, not every person on it at once.
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  const [sort, setSort] = useState({ key: 'project', dir: 'asc' });

  useEffect(() => {
    if (defaultPeriodMonth && selectedPeriods.length === 0) setSelectedPeriods([defaultPeriodMonth]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPeriodMonth]);

  // Same drop-what-the-year-no-longer-offers behavior as Hours by Category.
  useEffect(() => {
    setSelectedPeriods((prev) => {
      const kept = prev.filter((p) => periods.includes(p));
      if (kept.length > 0) return kept;
      return defaultPeriodMonth && periods.includes(defaultPeriodMonth) ? [defaultPeriodMonth] : [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods]);

  const currentFilters = { selectedPeriods, projectIds, projectTypes, teamIds, managerIds, userIds, baselineId, includeRnd };
  const { applied, isDirty, run: runReport } = useAppliedFilters(currentFilters, selectedPeriods.length > 0);

  const fcstVsActualsUrl = useMemo(() => {
    if (!applied || applied.selectedPeriods.length === 0) return null;
    const params = new URLSearchParams({ periodMonth: applied.selectedPeriods.join(',') });
    if (applied.projectIds.length > 0) params.set('projectId', applied.projectIds.join(','));
    if (applied.projectTypes.length > 0) params.set('projectType', applied.projectTypes.join(','));
    if (applied.teamIds.length > 0) params.set('teamId', applied.teamIds.join(','));
    if (applied.managerIds.length > 0) params.set('managerId', applied.managerIds.join(','));
    if (applied.userIds.length > 0) params.set('userId', applied.userIds.join(','));
    if (applied.baselineId) params.set('baselineId', applied.baselineId);
    if (applied.includeRnd) params.set('includeRnd', '1');
    return `/dashboard/reports/fcst-vs-actuals?${params}`;
  }, [applied]);
  const { data, loading, error } = useReportFetch(fcstVsActualsUrl);

  const teamScopedUsers = useMemo(() => usersInTeams(users, teamIds), [users, teamIds]);
  const managers = useMemo(() => managersOf(teamScopedUsers), [teamScopedUsers]);
  const personOptions = useMemo(() => directReportsOf(teamScopedUsers, managerIds), [teamScopedUsers, managerIds]);

  function onTeamChange(value) {
    setTeamIds(value);
    setManagerIds([]);
    setUserIds([]);
  }

  function toggleSort(key) {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'project' ? 'asc' : 'desc' }));
  }

  function toggleProject(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleTeam(key) {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const showBaseline = Boolean(baselineId && data?.baseline);
  const periodCols = showBaseline ? 5 : 3;

  const rows = useMemo(() => {
    if (!data) return [];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...data.rows].sort((a, b) => {
      const av = fcstSortValue(a, sort.key);
      const bv = fcstSortValue(b, sort.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [data, sort]);

  // One shared scale across every project's bar, so bar lengths are comparable row to row.
  const barMax = useMemo(
    () => rows.reduce((max, r) => Math.max(max, r.total.fcst, r.total.act), 0),
    [rows],
  );

  function exportXlsx() {
    if (!data) return;
    const sheetRows = [];
    for (const row of rows) {
      const push = (name, level, cells, team = '') => {
        const out = { Project: row.projectName, Team: team, Row: name, Level: level };
        for (const period of data.periods) {
          const cell = cells.byPeriod[period.periodMonth];
          out[`${period.label} FCST`] = cell.fcst;
          out[`${period.label} ACT`] = cell.act;
          out[`${period.label} Var`] = variance(cell);
          if (showBaseline) {
            out[`${period.label} BL`] = cell.bl;
            out[`${period.label} BL Var`] = baselineVariance(cell);
          }
        }
        out['Total FCST'] = cells.total.fcst;
        out['Total ACT'] = cells.total.act;
        out['Total Var'] = variance(cells.total);
        if (showBaseline) {
          out['Total BL'] = cells.total.bl;
          out['Total BL Var'] = baselineVariance(cells.total);
        }
        sheetRows.push(out);
      };
      push('— all —', 'Project', row);
      for (const team of row.teams) {
        push('— all —', 'Team', team, team.teamName);
        for (const person of team.people) push(person.name, ROW_LEVEL_LABELS[person.kind], person, team.teamName);
      }
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetRows), 'FCST vs Actuals');
    // Named from `applied`, not the live (possibly staged-but-not-run) selection -- see the same note
    // on Hours by Category's exportXlsx above.
    XLSX.writeFile(wb, `FCST vs Actuals ${safeFileSegment(applied.selectedPeriods.join(' '))}.xlsx`);
  }

  const overall = data?.totals.overall;
  const misalignedPeriods = (data?.periods || []).filter((p) => !p.calendarAligned).map((p) => p.label);

  return (
    <div>
      <ErrorBanner error={error} />
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <YearSelector years={years} year={year} onChange={onYearChange} />
        <PeriodMultiSelect periods={periods} selected={selectedPeriods} onChange={setSelectedPeriods} />
        <OptionMultiSelect label="Project(s)" options={projects} selected={projectIds} onChange={setProjectIds} allLabel="All projects" noun="projects" width={200} />
        <OptionMultiSelect label="Project type(s)" options={PROJECT_TYPE_OPTIONS} selected={projectTypes} onChange={setProjectTypes} allLabel="All types" noun="types" width={130} />
        <OptionMultiSelect label="Team(s)" options={teams} selected={teamIds} onChange={onTeamChange} allLabel="All teams" noun="teams" />
        <OptionMultiSelect label="Manager(s)" options={managers} selected={managerIds} onChange={(value) => { setManagerIds(value); setUserIds([]); }} allLabel="All managers" noun="managers" />
        <OptionMultiSelect label="Person(s)" options={personOptions} selected={userIds} onChange={setUserIds} allLabel="All people" noun="people" />
        <div className="form-group" style={{ marginBottom: 0, width: 170 }}>
          <label className="form-label">Compare to baseline</label>
          <select className="form-select" value={baselineId} onChange={(e) => setBaselineId(e.target.value)}>
            <option value="">Forecast only</option>
            {baselines.map((b) => <option key={b.id} value={b.id}>{b.year} {b.period}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-mid)', marginBottom: 6 }}>
          <input type="checkbox" checked={includeRnd} onChange={(e) => setIncludeRnd(e.target.checked)} />
          Include R&amp;D
          <Link to="/rnd-time" style={{ fontSize: 11 }}>(enter)</Link>
        </label>
        <RunReportBar isDirty={isDirty} onRun={runReport} disabled={selectedPeriods.length === 0} />
      </div>

      {loading ? (
        <div className="card">Loading… this can take several seconds for a wide date range or several teams.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <MetricTile label="Forecast (FCST)" value={overall?.fcst ?? 0} sub="current plan" />
            <MetricTile label="Actual" value={overall?.act ?? 0} sub="hours logged" tone="var(--green)" />
            <MetricTile
              label="Variance"
              value={fmtSigned(variance(overall || { act: 0, fcst: 0 })) || '0'}
              sub="actual − forecast"
              tone={varianceStyle(variance(overall || { act: 0, fcst: 0 })).color}
            />
            <MetricTile label="Achievement" value={achievementPct(overall?.act, overall?.fcst) != null ? `${achievementPct(overall.act, overall.fcst)}%` : '—'} sub="actual ÷ forecast" />
            <MetricTile label="Accuracy" value={accuracyPct(overall?.act, overall?.fcst) != null ? `${accuracyPct(overall.act, overall.fcst)}%` : '—'} sub="closeness, either way" />
            {showBaseline && (
              <>
                <MetricTile label={`Baseline (${data.baseline.year} ${data.baseline.period})`} value={overall?.bl ?? 0} sub="locked plan" />
                <MetricTile
                  label="BL variance"
                  value={fmtSigned(baselineVariance(overall || { act: 0, bl: 0 })) || '0'}
                  sub="actual − baseline"
                  tone={varianceStyle(baselineVariance(overall || { act: 0, bl: 0 })).color}
                />
                <MetricTile label="BL achievement" value={achievementPct(overall?.act, overall?.bl) != null ? `${achievementPct(overall.act, overall.bl)}%` : '—'} sub="actual ÷ baseline" />
              </>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
            <button className="btn btn-outline" onClick={() => window.print()}>Print / Save as PDF</button>
            <button className="btn btn-primary" onClick={exportXlsx}>Export to Excel</button>
          </div>

          <div className="sheet-wrap">
            <table className="sheet">
              <thead>
                <tr>
                  {/* Frozen so the project/team/person name stays visible once several periods push
                      the grid past the viewport (Jeremy, 2026-08-21). Width pinned, not just hinted,
                      so a long project name can't stretch the column out from under `left: 0`. */}
                  <th rowSpan={2} className="is-frozen is-frozen-edge" style={{ ...FROZEN_NAME_COL, textAlign: 'left', left: 0 }} />
                  {(data?.periods || []).map((p) => (
                    <th key={p.periodMonth} colSpan={periodCols} style={{ textAlign: 'center', borderLeft: '1px solid var(--border)' }}>
                      {p.label} <span style={{ fontWeight: 400, color: 'var(--text-light)' }}>({p.periodMonth})</span>
                    </th>
                  ))}
                  <th colSpan={periodCols + 1} style={{ textAlign: 'center', borderLeft: '1px solid var(--border)' }}>Total</th>
                </tr>
                <tr>
                  {(data?.periods || []).map((p) => (
                    <React.Fragment key={p.periodMonth}>
                      <th style={{ textAlign: 'center', width: 60, borderLeft: '1px solid var(--border)' }}>FCST</th>
                      <th style={{ textAlign: 'center', width: 60 }}>ACT</th>
                      <th style={{ textAlign: 'center', width: 60 }}>Var</th>
                      {showBaseline && <th style={{ textAlign: 'center', width: 60 }}>BL</th>}
                      {showBaseline && <th style={{ textAlign: 'center', width: 70 }}>BL Var</th>}
                    </React.Fragment>
                  ))}
                  <SortTh label="FCST" sortKey="fcst" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 70, borderLeft: '1px solid var(--border)' }} />
                  <SortTh label="ACT" sortKey="act" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 70 }} />
                  <SortTh label="Var" sortKey="var" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 70 }} />
                  {showBaseline && <SortTh label="BL" sortKey="bl" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 70 }} />}
                  {showBaseline && <SortTh label="BL Var" sortKey="blvar" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 80 }} />}
                  <th style={{ width: 90 }}>FCST / ACT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const open = expanded.has(row.projectId);
                  const cells = (source, isProject) => (
                    <>
                      {(data?.periods || []).map((p) => {
                        const cell = source.byPeriod[p.periodMonth];
                        return (
                          <React.Fragment key={p.periodMonth}>
                            <td className="cell-num" style={{ borderLeft: '1px solid var(--border)' }}>{fmt(cell.fcst)}</td>
                            <td className="cell-num">{fmt(cell.act)}</td>
                            <td className="cell-num" style={varianceStyle(variance(cell))}>{fmtSigned(variance(cell))}</td>
                            {showBaseline && <td className="cell-num">{fmt(cell.bl)}</td>}
                            {showBaseline && <td className="cell-num" style={varianceStyle(baselineVariance(cell))}>{fmtSigned(baselineVariance(cell))}</td>}
                          </React.Fragment>
                        );
                      })}
                      <td className="cell-num" style={{ borderLeft: '1px solid var(--border)', fontWeight: isProject ? 700 : 400 }}>{fmt(source.total.fcst)}</td>
                      <td className="cell-num" style={{ fontWeight: isProject ? 700 : 400 }}>{fmt(source.total.act)}</td>
                      <td className="cell-num" style={{ ...varianceStyle(variance(source.total)) }}>{fmtSigned(variance(source.total))}</td>
                      {showBaseline && <td className="cell-num" style={{ fontWeight: isProject ? 700 : 400 }}>{fmt(source.total.bl)}</td>}
                      {showBaseline && <td className="cell-num" style={varianceStyle(baselineVariance(source.total))}>{fmtSigned(baselineVariance(source.total))}</td>}
                      <td style={{ padding: '8px 10px' }}>{isProject && <PlanActualBar planned={source.total.fcst} act={source.total.act} max={barMax} />}</td>
                    </>
                  );
                  return (
                    <React.Fragment key={row.projectId}>
                      <tr className="is-group" onClick={() => toggleProject(row.projectId)} style={{ cursor: 'pointer' }}>
                        <td className="is-frozen is-frozen-edge" style={{ ...FROZEN_NAME_COL, padding: '8px 10px', fontWeight: 700, left: 0 }}>
                          <span style={{ display: 'inline-block', width: 14, opacity: 0.5 }}>{open ? '▾' : '▸'}</span>
                          {row.projectName}
                        </td>
                        {cells(row, true)}
                      </tr>
                      {open && row.teams.map((team) => {
                        const teamKey = `${row.projectId}::${team.teamId}`;
                        const teamOpen = expandedTeams.has(teamKey);
                        return (
                          <React.Fragment key={teamKey}>
                            <tr className="is-group-sub" onClick={() => toggleTeam(teamKey)} style={{ cursor: 'pointer' }}>
                              <td className="is-frozen is-frozen-edge" style={{ ...FROZEN_NAME_COL, padding: '6px 10px 6px 26px', left: 0 }}>
                                <span style={{ display: 'inline-block', width: 14, opacity: 0.5 }}>{teamOpen ? '▾' : '▸'}</span>
                                {team.teamName}
                              </td>
                              {cells(team, false)}
                            </tr>
                            {teamOpen && team.people.map((person) => (
                              <tr key={person.key}>
                                <td className="is-frozen is-frozen-edge" style={{ ...FROZEN_NAME_COL, padding: '6px 10px 6px 58px', color: 'var(--text-mid)', fontStyle: person.kind === 'team' ? 'italic' : 'normal', left: 0 }}>
                                  {person.name}
                                </td>
                                {cells(person, false)}
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={(data?.periods.length || 0) * periodCols + periodCols + 2} style={{ padding: 16, textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      No forecast or actual hours for this selection.
                    </td>
                  </tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="is-group is-total">
                    <td className="is-frozen is-frozen-edge" style={{ ...FROZEN_NAME_COL, padding: '8px 10px', fontWeight: 700, left: 0 }}>Total</td>
                    {cells0(data, showBaseline)}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className="sheet-note">
            Billable delivery only — projects on the Internal account (including Administrative Time) are excluded, and
            Actuals count only task types whose <strong>Activity Billed To</strong> is Project (so Market, Core Product
            and Administrative time are out), minus non-billable-productive ones.
            Rows group <strong>project → team → person</strong>; a person sits under their current team.
            <strong> FCST</strong> is the live forecast — a person's monthly hours where they're assigned, otherwise the
            team's unassigned plan for that project (never both); a Manager or Person filter excludes unassigned team
            plans, which can't be attributed to one person.
            {/* Only shown when a period's fiscal weeks actually straddle a calendar month -- true of some
                FY25 history, and of no period that has a forecast to compare against. */}
            {misalignedPeriods.length > 0 && (
              <span className="sheet-note-caveat">
                {' '}{misalignedPeriods.join(', ')} {misalignedPeriods.length === 1 ? 'has' : 'have'} fiscal weeks that
                cross a calendar month boundary, so a few actual hours sit in a different column from the forecast
                they belong to.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// The grand-total row's cells. Pulled out of the component because it renders the same shape as the
// per-row cells but off `totals`, which has no `people` to expand.
function cells0(data, showBaseline) {
  if (!data) return null;
  const t = data.totals;
  return (
    <>
      {data.periods.map((p) => {
        const cell = t.byPeriod[p.periodMonth];
        return (
          <React.Fragment key={p.periodMonth}>
            <td className="cell-num" style={{ borderLeft: '1px solid var(--border)' }}>{fmt(cell.fcst)}</td>
            <td className="cell-num">{fmt(cell.act)}</td>
            <td className="cell-num" style={varianceStyle(variance(cell))}>{fmtSigned(variance(cell))}</td>
            {showBaseline && <td className="cell-num">{fmt(cell.bl)}</td>}
            {showBaseline && <td className="cell-num" style={varianceStyle(baselineVariance(cell))}>{fmtSigned(baselineVariance(cell))}</td>}
          </React.Fragment>
        );
      })}
      <td className="cell-num" style={{ borderLeft: '1px solid var(--border)', fontWeight: 700 }}>{fmt(t.overall.fcst)}</td>
      <td className="cell-num" style={{ fontWeight: 700 }}>{fmt(t.overall.act)}</td>
      <td className="cell-num" style={varianceStyle(variance(t.overall))}>{fmtSigned(variance(t.overall))}</td>
      {showBaseline && <td className="cell-num" style={{ fontWeight: 700 }}>{fmt(t.overall.bl)}</td>}
      {showBaseline && <td className="cell-num" style={varianceStyle(baselineVariance(t.overall))}>{fmtSigned(baselineVariance(t.overall))}</td>}
      <td />
    </>
  );
}

// ---------- Finance Accounting export ----------

function groupRowsByProject(rows) {
  const groups = [];
  let current = null;
  for (const row of rows) {
    if (!current || current.projectName !== row.projectName) {
      current = { projectName: row.projectName, rows: [] };
      groups.push(current);
    }
    current.rows.push(row);
  }
  return groups;
}

// Per-project subtotals shown on the same row as the project name, Excel-pivot style (Jeremy,
// 2026-08-10).
export function projectGroupTotals(group, weeks) {
  const byWeek = {};
  let total = 0;
  for (const row of group.rows) {
    for (const week of weeks) {
      const hours = row.hoursByWeek[week.periodName];
      if (hours) byWeek[week.periodName] = Math.round((byWeek[week.periodName] || 0) * 100 + hours * 100) / 100;
    }
    total = Math.round((total + row.total) * 100) / 100;
  }
  return { byWeek, total };
}

function AccountingTab({ periodMonth, periods, onPeriodChange, years, year, onYearChange }) {
  const { currentUser } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const [locking, setLocking] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Project names, not ids -- that's all the report returns, and they're unique per group.
  const [collapsed, setCollapsed] = useState(() => new Set()); // empty = expanded, the default

  useEffect(() => {
    if (!periodMonth) return;
    setLoading(true);
    setError('');
    apiFetch(`/dashboard/reports/accounting?periodMonth=${periodMonth}`)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [periodMonth]);

  // Lock month closes every timesheet in the month to entry, approval and recall; only an admin can
  // lock or unlock, and this is the only unlock path.
  async function setMonthLocked(lock) {
    const verb = lock ? 'lock' : 'unlock';
    const msg = lock
      ? `Lock ${data.periodMonth}? Every timesheet in the month closes: nobody can add, change, submit, approve or recall hours until an admin unlocks it.`
      : `Unlock ${data.periodMonth}? Its timesheets become editable again.`;
    if (!window.confirm(msg)) return;
    setLocking(true);
    setError('');
    try {
      await apiFetch(`/fiscal-weeks/month/${data.periodMonth}/${verb}`, { method: 'POST' });
      setData((d) => ({ ...d, locked: lock }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLocking(false);
    }
  }

  function exportXlsx() {
    if (!data) return;
    const sheetRows = data.rows.map((row) => {
      const out = { 'Project Name': row.projectName, 'Resource Name': row.resourceName };
      for (const week of data.weeks) out[week.periodName] = row.hoursByWeek[week.periodName] || '';
      out['Total'] = row.total;
      return out;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetRows), 'Accounting');
    XLSX.writeFile(wb, `Accounting ${safeFileSegment(data.periodMonth)}.xlsx`);
  }

  const groups = data ? groupRowsByProject(data.rows) : [];

  function toggle(projectName) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(projectName)) next.delete(projectName);
      else next.add(projectName);
      return next;
    });
  }

  return (
    <div>
      <ErrorBanner error={error} />
      {/* Year/period sit here rather than in the page header (Jeremy, 2026-08-10) -- left-aligned
          directly above the grid they filter, with the export buttons keeping the right edge. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <YearSelector years={years} year={year} onChange={onYearChange} />
          {periodMonth && <PeriodSelector periods={periods} periodMonth={periodMonth} onChange={onPeriodChange} />}
          {groups.length > 0 && (
            <div style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
              <button className="btn btn-outline" onClick={() => setCollapsed(new Set())}>Expand all</button>
              <button className="btn btn-outline" onClick={() => setCollapsed(new Set(groups.map((g) => g.projectName)))}>Collapse all</button>
            </div>
          )}
        </div>
        {data && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {data.locked && <span className="badge badge-yellow">Month locked</span>}
            {isAdmin && (
              <button className="btn btn-outline" disabled={locking} onClick={() => setMonthLocked(!data.locked)}>
                {data.locked ? 'Unlock month' : 'Lock month'}
              </button>
            )}
            <button className="btn btn-outline" onClick={() => window.print()}>Print / Save as PDF</button>
            <button className="btn btn-primary" onClick={exportXlsx}>Export to Excel</button>
          </div>
        )}
      </div>
      {loading ? <div className="card">Loading…</div> : (
      // fit-content rather than the .sheet default full width (Jeremy, 2026-08-10) -- a month is
      // 4-5 narrow week columns, and stretching left the label column absurdly wide.
      <div className="sheet-wrap" style={{ width: 'fit-content', maxWidth: '100%' }}>
        <table className="sheet">
          <thead>
            <tr>
              <th style={{ textAlign: 'left', minWidth: 220 }}>Project / Resource</th>
              {(data?.weeks || []).map((w) => (
                <th key={w.periodName} style={{ textAlign: 'center', width: 80, borderLeft: '1px solid var(--border)' }}>{w.periodName}</th>
              ))}
              <th style={{ textAlign: 'center', width: 80, borderLeft: '1px solid var(--border)' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group, gi) => {
              const subtotal = projectGroupTotals(group, data?.weeks || []);
              const alt = gi % 2 === 1 ? 'is-alt' : '';
              const isCollapsed = collapsed.has(group.projectName);
              return (
                <React.Fragment key={group.projectName}>
                  {/* One label column, resources nested under their project (Jeremy, 2026-08-10) --
                      the project row carries its own totals, so it needs no separate label. */}
                  <tr className={alt} onClick={() => toggle(group.projectName)} style={{ cursor: 'pointer' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700 }}>
                      <span style={{ color: 'var(--text-mid)', fontSize: 10, marginRight: 6 }}>{isCollapsed ? '▶' : '▼'}</span>
                      {group.projectName}
                    </td>
                    {(data?.weeks || []).map((w) => (
                      <td key={w.periodName} className="cell-num" style={{ fontWeight: 600, borderLeft: '1px solid var(--border)' }}>
                        {subtotal.byWeek[w.periodName] || ''}
                      </td>
                    ))}
                    <td className="cell-num" style={{ fontWeight: 700, borderLeft: '1px solid var(--border)' }}>{subtotal.total}</td>
                  </tr>
                  {!isCollapsed && group.rows.map((row) => (
                    <tr key={`${group.projectName}|${row.resourceName}`} className={alt}>
                      <td style={{ padding: '8px 10px 8px 34px', color: 'var(--text-mid)' }}>{row.resourceName}</td>
                      {(data?.weeks || []).map((w) => (
                        <td key={w.periodName} className="cell-num" style={{ borderLeft: '1px solid var(--border)' }}>
                          {numBlank(row.hoursByWeek[w.periodName])}
                        </td>
                      ))}
                      <td className="cell-num" style={{ fontWeight: 600, borderLeft: '1px solid var(--border)' }}>{num(row.total)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={(data.weeks.length || 0) + 2} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                  No hours logged this period.
                </td>
              </tr>
            )}
            {data && data.rows.length > 0 && (
              <tr className="is-group">
                <td>Grand Total</td>
                {data.weeks.map((w) => (
                  <td key={w.periodName} className="cell-num" style={{ borderLeft: '1px solid var(--border)' }}>
                    {numBlank(Math.round(data.rows.reduce((sum, r) => sum + (r.hoursByWeek[w.periodName] || 0), 0) * 100) / 100)}
                  </td>
                ))}
                <td className="cell-num" style={{ borderLeft: '1px solid var(--border)' }}>{num(data.grandTotal)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// ---------- Timesheet Compliance ----------

export function complianceSortValue(row, key) {
  switch (key) {
    case 'team': return (row.teamName || '').toLowerCase();
    case 'manager': return (row.managerName || '').toLowerCase();
    default: return (row.resourceName || '').toLowerCase();
  }
}

function ComplianceTab({ periodMonth, periods, onPeriodChange, years, year, onYearChange, teams, users }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters are ids (matching Hours by Category's cascade), translated to the display names
  // ReportsService.compliance actually returns rows keyed by (see below).
  const [teamIds, setTeamIds] = usePersistedFilter('timeReports.compliance.teamIds', []);
  const [managerId, setManagerId] = usePersistedFilter('timeReports.compliance.managerId', '');
  // "Has this status in any week of the period" (Jeremy, 2026-08-10) -- the question being asked is
  // who still needs chasing, and a person is worth chasing if any one of their weeks is behind.
  const [status, setStatus] = usePersistedFilter('timeReports.compliance.status', '');
  // Team is the default sort (Jeremy, 2026-08-06) -- was a fixed Manager-grouped layout before;
  // sortable-by-any-column and a fixed rowSpan grouping don't mix, so this is now a flat table like
  // Configuration's People tab, sorted rather than visually grouped.
  const [sort, setSort] = useState({ key: 'team', dir: 'asc' });

  useEffect(() => {
    if (!periodMonth) return;
    setLoading(true);
    setError('');
    apiFetch(`/dashboard/reports/compliance?periodMonth=${periodMonth}`)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [periodMonth]);

  // Cascading options, same as Hours by Category: Team narrows the Manager pool to managers who
  // are themselves on that team.
  const teamScopedUsers = useMemo(() => usersInTeams(users, teamIds), [users, teamIds]);
  const managers = useMemo(() => managersOf(teamScopedUsers), [teamScopedUsers]);

  function onTeamChange(value) {
    setTeamIds(value);
    setManagerId('');
  }

  function toggleSort(key) {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }

  // The report's rows only carry display names (teamName/managerName), not ids -- translate the
  // filter's selected ids to names once here rather than re-deriving them inside the filter loop.
  const teamNames = useMemo(
    () => new Set(teamIds.map((id) => teams.find((t) => t.id === id)?.name).filter(Boolean)),
    [teams, teamIds],
  );
  const managerName = useMemo(() => users.find((u) => u.id === managerId)?.name || '', [users, managerId]);

  const rows = useMemo(() => {
    if (!data) return [];
    const filtered = data.rows.filter(
      (r) => (teamNames.size === 0 || teamNames.has(r.teamName))
        && (!managerName || r.managerName === managerName)
        && (!status || data.weeks.some((w) => r.statusByWeek[w.periodName] === status)),
    );
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = complianceSortValue(a, sort.key);
      const bv = complianceSortValue(b, sort.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [data, teamNames, managerName, status, sort]);

  if (loading) return <div className="card">Loading…</div>;

  return (
    <div>
      <ErrorBanner error={error} />
      {/* Year/period lead the filter row rather than sitting in the page header (Jeremy, 2026-08-10)
          -- same move Accounting made, and they read as the outermost filter so they come first. */}
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <YearSelector years={years} year={year} onChange={onYearChange} />
        {periodMonth && <PeriodSelector periods={periods} periodMonth={periodMonth} onChange={onPeriodChange} />}
        <OptionMultiSelect label="Team(s)" options={teams} selected={teamIds} onChange={onTeamChange} allLabel="All teams" noun="teams" />
        <div className="form-group" style={{ marginBottom: 0, width: 180 }}>
          <label className="form-label">Resource Manager</label>
          <select className="form-select" value={managerId} onChange={(e) => setManagerId(e.target.value)}>
            <option value="">All managers</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0, width: 180 }}>
          <label className="form-label">Status</label>
          <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.keys(STATUS_STYLES).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, fontSize: 12 }}>
        {Object.entries(STATUS_STYLES).map(([label, style]) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: style.color, display: 'inline-block' }} />
            {label}
          </span>
        ))}
      </div>
      {/* fit-content rather than the .sheet default full width (Jeremy, 2026-08-10) -- a month is
          4-5 narrow status columns, and stretching stranded them at the far right of the page. */}
      <div className="sheet-wrap" style={{ width: 'fit-content', maxWidth: '100%' }}>
        <table className="sheet">
          <thead>
            <tr>
              <SortTh label="Team" sortKey="team" sort={sort} onSort={toggleSort} style={{ minWidth: 140 }} />
              <SortTh label="Resource Manager" sortKey="manager" sort={sort} onSort={toggleSort} style={{ minWidth: 160 }} />
              <SortTh label="Resource Name" sortKey="resource" sort={sort} onSort={toggleSort} style={{ minWidth: 160 }} />
              {(data?.weeks || []).map((w) => (
                <th key={w.periodName} style={{ textAlign: 'center', width: 100, borderLeft: '1px solid var(--border)' }}>{w.periodName}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.teamName}|${row.managerName}|${row.resourceName}`} className={i % 2 === 1 ? 'is-alt' : ''}>
                <td style={{ padding: '8px 10px', color: 'var(--text-mid)' }}>{row.teamName}</td>
                <td style={{ padding: '8px 10px', color: 'var(--text-mid)' }}>{row.managerName}</td>
                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{row.resourceName}</td>
                {(data?.weeks || []).map((w) => {
                  const status = row.statusByWeek[w.periodName];
                  const style = STATUS_STYLES[status] || {};
                  return (
                    <td key={w.periodName} style={{ borderLeft: '1px solid var(--border)', textAlign: 'center', padding: '4px' }}>
                      <span style={{ ...style, display: 'inline-block', width: '100%', padding: '4px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                        {status}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={(data?.weeks.length || 0) + 3} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                  No active people match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Sightline Feed ----------

// Sightline lists projects by contract type in this order, so the grid matches the screen being keyed.
const SIGHTLINE_TYPES = ['POC', 'POC (Milestone)', 'T&M', 'B&B'];
const SIGHTLINE_TYPE_OPTIONS = SIGHTLINE_TYPES.map((t) => ({ id: t, name: t }));

// Groups the API's already-sorted rows by contract type, with a subtotal per group.
export function groupSightlineRows(rows) {
  return SIGHTLINE_TYPES
    .map((type) => {
      const items = rows.filter((r) => r.projectType === type);
      return {
        type,
        items,
        psHours: Math.round(items.reduce((s, r) => s + r.psHours, 0) * 100) / 100,
        rndHours: Math.round(items.reduce((s, r) => s + r.rndHours, 0) * 100) / 100,
      };
    })
    .filter((g) => g.items.length > 0);
}

function dash(n) {
  return n ? num(n) : '—';
}

// The user's own project order (Jeremy, 2026-10-07) -- lets them mirror the row order of the Sightline
// screen they're keying into. localStorage, not session: it should survive tomorrow's run.
const SIGHTLINE_ORDER_KEY = 'timeReports.sightline.order';
function loadSightlineOrder() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SIGHTLINE_ORDER_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Sorts a group's rows by the saved order; projects not in it yet keep the API's (alphabetical)
// order after the ones that are.
export function applySightlineOrder(items, order) {
  const rank = new Map(order.map((id, i) => [id, i]));
  return items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => (rank.get(a.item.projectId) ?? Infinity) - (rank.get(b.item.projectId) ?? Infinity) || a.i - b.i)
    .map((x) => x.item);
}

function SightlineFeedTab({ periods, defaultPeriodMonth, projects, years, year, onYearChange }) {
  const [selectedPeriods, setSelectedPeriods] = useRememberedPeriod('timeReports.sightline.selectedPeriods', []);
  const [projectIds, setProjectIds] = usePersistedFilter('timeReports.sightline.projectIds', []);
  const [projectTypes, setProjectTypes] = usePersistedFilter('timeReports.sightline.projectTypes', []);

  useEffect(() => {
    if (defaultPeriodMonth && selectedPeriods.length === 0) setSelectedPeriods([defaultPeriodMonth]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPeriodMonth]);

  useEffect(() => {
    setSelectedPeriods((prev) => {
      const kept = prev.filter((p) => periods.includes(p));
      if (kept.length > 0) return kept;
      return defaultPeriodMonth && periods.includes(defaultPeriodMonth) ? [defaultPeriodMonth] : [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods]);

  const { applied, isDirty, run: runReport } = useAppliedFilters({ selectedPeriods, projectIds, projectTypes }, selectedPeriods.length > 0);

  const url = useMemo(() => {
    if (!applied || applied.selectedPeriods.length === 0) return null;
    const params = new URLSearchParams({ periodMonth: applied.selectedPeriods.join(',') });
    if (applied.projectIds.length > 0) params.set('projectId', applied.projectIds.join(','));
    if (applied.projectTypes.length > 0) params.set('projectType', applied.projectTypes.join(','));
    return `/dashboard/reports/sightline-feed?${params}`;
  }, [applied]);
  const { data, loading, error } = useReportFetch(url);

  const [order, setOrder] = useState(loadSightlineOrder);
  const groups = useMemo(
    () => groupSightlineRows(data?.rows || []).map((g) => ({ ...g, items: applySightlineOrder(g.items, order) })),
    [data, order],
  );

  // Swaps a project with its neighbor inside its own type group. Saves the whole on-screen order so
  // projects that weren't yet in the saved list are pinned where they now appear.
  function moveProject(type, projectId, delta) {
    const shown = groups.flatMap((g) => g.items.map((r) => r.projectId));
    const group = groups.find((g) => g.type === type).items.map((r) => r.projectId);
    const from = group.indexOf(projectId);
    const to = from + delta;
    if (to < 0 || to >= group.length) return;
    [group[from], group[to]] = [group[to], group[from]];
    const next = [...groups.flatMap((g) => (g.type === type ? group : g.items.map((r) => r.projectId))),
      ...order.filter((id) => !shown.includes(id))];
    setOrder(next);
    try { localStorage.setItem(SIGHTLINE_ORDER_KEY, JSON.stringify(next)); } catch { /* order just won't persist */ }
  }

  function exportXlsx() {
    if (!data) return;
    const period = applied.selectedPeriods.join(', ');
    const sheetRows = groups.flatMap((g) => g.items.map((r) => ({
      Period: period,
      'Project Type': r.projectType,
      'Project ID': r.projectId,
      Project: r.projectName,
      Manager: r.manager,
      'PS Hours': r.psHours,
      'R&D Hours': r.rndHours,
      'Total Hours': r.totalHours,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetRows), 'Sightline Feed');
    XLSX.writeFile(wb, `Sightline Feed ${safeFileSegment(applied.selectedPeriods.join(' '))}.xlsx`);
  }

  const totals = data?.totals;
  const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div>
      <ErrorBanner error={error} />
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <YearSelector years={years} year={year} onChange={onYearChange} />
        <PeriodMultiSelect periods={periods} selected={selectedPeriods} onChange={setSelectedPeriods} />
        <OptionMultiSelect label="Project(s)" options={projects} selected={projectIds} onChange={setProjectIds} allLabel="All projects" noun="projects" width={200} />
        <OptionMultiSelect label="Project type(s)" options={SIGHTLINE_TYPE_OPTIONS} selected={projectTypes} onChange={setProjectTypes} allLabel="All types" noun="types" width={130} />
        <RunReportBar isDirty={isDirty} onRun={runReport} disabled={selectedPeriods.length === 0} />
      </div>

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          {/* Shrink-wrapped so the export button sits on the grid's own top-right corner. */}
          <div style={{ width: 'fit-content', maxWidth: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary" onClick={exportXlsx} disabled={!data || groups.length === 0}>Export to Excel</button>
          </div>

          <div className="sheet-wrap is-fit">
            <table className="sheet">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', minWidth: 240 }}>Project</th>
                  <th style={{ textAlign: 'left', width: 70 }}>Type</th>
                  <th style={{ textAlign: 'left', width: 160 }}>Manager</th>
                  <th style={{ ...num, width: 100 }}>PS hrs</th>
                  <th style={{ ...num, width: 100 }}>R&amp;D hrs</th>
                  <th style={{ ...num, width: 100 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.type}>
                    <tr className="is-group">
                      <td colSpan={3} style={{ padding: '8px 10px', fontWeight: 700 }}>
                        {g.type} <span style={{ fontWeight: 400, color: 'var(--text-light)' }}>· {g.items.length} {g.items.length === 1 ? 'project' : 'projects'}</span>
                      </td>
                      <td className="cell-num" style={{ fontWeight: 700 }}>{num(g.psHours)}</td>
                      <td className="cell-num" style={{ fontWeight: 700 }}>{num(g.rndHours)}</td>
                      <td className="cell-num" style={{ fontWeight: 700 }}>{num(Math.round((g.psHours + g.rndHours) * 100) / 100)}</td>
                    </tr>
                    {g.items.map((r, idx) => (
                      <tr key={r.projectId}>
                        <td style={{ padding: '6px 10px 6px 10px' }}>
                          <span className="no-print" style={{ display: 'inline-flex', flexDirection: 'column', marginRight: 8, verticalAlign: 'middle' }}>
                            <button type="button" aria-label={`Move ${r.projectName} up`} disabled={idx === 0} onClick={() => moveProject(g.type, r.projectId, -1)}
                              style={{ border: 'none', background: 'none', padding: 0, lineHeight: '10px', fontSize: 10, cursor: idx === 0 ? 'default' : 'pointer', color: 'var(--text-light)', opacity: idx === 0 ? 0.3 : 1 }}>▲</button>
                            <button type="button" aria-label={`Move ${r.projectName} down`} disabled={idx === g.items.length - 1} onClick={() => moveProject(g.type, r.projectId, 1)}
                              style={{ border: 'none', background: 'none', padding: 0, lineHeight: '10px', fontSize: 10, cursor: idx === g.items.length - 1 ? 'default' : 'pointer', color: 'var(--text-light)', opacity: idx === g.items.length - 1 ? 0.3 : 1 }}>▼</button>
                          </span>
                          {r.projectName}
                        </td>
                        <td style={{ padding: '6px 10px' }}>{r.projectType}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--text-mid)' }}>{r.manager || '—'}</td>
                        <td className="cell-num">{dash(r.psHours)}</td>
                        <td className="cell-num">{dash(r.rndHours)}</td>
                        <td className="cell-num" style={{ fontWeight: 600 }}>{num(r.totalHours)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {groups.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 16, textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      No PS or R&amp;D hours for this selection.
                    </td>
                  </tr>
                )}
              </tbody>
              {groups.length > 0 && (
                <tfoot>
                  <tr className="is-group is-total">
                    <td colSpan={3} style={{ padding: '8px 10px', fontWeight: 700 }}>All projects</td>
                    <td className="cell-num" style={{ fontWeight: 700 }}>{totals.psHours}</td>
                    <td className="cell-num" style={{ fontWeight: 700 }}>{totals.rndHours}</td>
                    <td className="cell-num" style={{ fontWeight: 700 }}>{totals.totalHours}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          </div>
          <div className="sheet-note">
            For keying into Sightline. <strong>PS hours</strong> are billable delivery only, the same rule as FCST vs
            Actuals (Internal-account projects, Market, Core Product and Administrative time are out).{' '}
            <strong>R&amp;D hours</strong> are the actuals entered on R&amp;D Time Entry. Projects with no hours in the
            selected periods are left off.
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Report: ACT Year over Year ----------

const fmtH = (n) => (n == null || !Number.isFinite(n) ? '—' : num(Math.round(n * 10) / 10));
const fmtPct = (n) => (n == null || !Number.isFinite(n) ? '—' : `${Math.round(n)}%`);
function deltaPct(c, p) {
  if (!p) return c ? 'new' : '—';
  const d = ((c - p) / p) * 100;
  return `${d >= 0 ? '▲' : '▼'} ${Math.abs(d).toFixed(1)}%`;
}
function DeltaPts({ c, p }) {
  if (c == null || p == null) return '—';
  const d = c - p;
  const color = Math.abs(d) < 0.5 ? 'var(--text-mid)' : d > 0 ? 'var(--green)' : 'var(--red)';
  return <span style={{ color }}>{d >= 0 ? '+' : '−'}{Math.abs(d).toFixed(1)} pts</span>;
}
const signed = (d) => `${d >= 0 ? '+' : '−'}${fmtH(Math.abs(d))}`;
const TDR = { padding: '7px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' };
const TDL = { ...TDR, textAlign: 'left' };
const THR = { textAlign: 'right' };
const PY_SUB = { display: 'block', fontSize: 11, color: 'var(--text-light)' };

function Swatch({ c }) {
  return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: CAT_COLOR[c], marginRight: 6, verticalAlign: -1 }} />;
}
function MixBar({ o, faded }) {
  const t = yoyTotal(o) || 1;
  return (
    <div style={{ display: 'flex', gap: 1, height: faded ? 5 : 12, marginTop: faded ? 3 : 0, opacity: faded ? 0.45 : 1, borderRadius: 3, overflow: 'hidden', minWidth: 140 }}>
      {CATS.map((c) => (o[c] > 0 ? <span key={c} style={{ width: `${(o[c] / t) * 100}%`, background: CAT_COLOR[c] }} /> : null))}
    </div>
  );
}
function useSort(initial) {
  const [sort, setSort] = useState(initial);
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }));
  return [sort, onSort];
}
function niceScale(max) {
  if (max <= 0) return { max: 1, step: 1 };
  const raw = max / 4;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((x) => x * p).find((x) => x >= raw);
  return { max: Math.ceil(max / step) * step, step };
}

// Paired bars per month -- prior year faded on the left, current year on the right -- stacked by
// category. Native <title> tooltips keep this dependency-free.
function YoyMonthChart({ series, months, cy, py, hidden, measure, capOf, onMonth, height = 260 }) {
  const W = 720, H = height, L = 48, R = 8, T = 10, B = 28;
  const vis = CATS.filter((c) => !hidden.includes(c));
  const val = (y, m, o, c) => (measure === 'pct' ? pct(o[c], capOf(y, m)) || 0 : o[c]);
  let mx = 0;
  for (const m of months) for (const [y, arr] of [[cy, series.cy], [py, series.py]]) mx = Math.max(mx, vis.reduce((a, c) => a + val(y, m, arr[m], c), 0));
  const { max, step } = niceScale(mx);
  const pw = W - L - R, ph = H - T - B, gw = pw / months.length, bw = Math.max(5, Math.min(22, gw * 0.32));
  const Y = (v) => T + ph - (v / max) * ph;
  const ticks = [];
  for (let v = 0; v <= max + 1e-9; v += step) ticks.push(v);
  const tipFor = (m) => [`${MONTHS[m]}: ${cy} / ${py}`, ...vis.map((c) => `${CAT_LABEL[c]}: ${fmtH(series.cy[m][c])} / ${fmtH(series.py[m][c])}`),
    `ACT: ${fmtH(yoyTotal(series.cy[m]))} / ${fmtH(yoyTotal(series.py[m]))}`].join('\n');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label={`Hours by month, ${cy} against ${py}`}>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} stroke="var(--border)" strokeOpacity={0.6} />
          <text x={L - 6} y={Y(v) + 4} textAnchor="end" fontSize={11} fill="var(--text-light)">{measure === 'pct' ? `${Math.round(v)}%` : num(v)}</text>
        </g>
      ))}
      {months.map((m, i) => {
        const cx = L + gw * i + gw / 2;
        return (
          <g key={m}>
            {[[py, series.py[m], cx - bw - 1, 0.42], [cy, series.cy[m], cx + 1, 1]].map(([y, o, x, op]) => {
              let acc = 0;
              return vis.map((c) => {
                const v = val(y, m, o, c);
                if (v <= 0) return null;
                const y1 = Y(acc + v), y0 = Y(acc);
                const gap = acc > 0 ? 1.5 : 0;
                acc += v;
                return <rect key={`${y}${c}`} x={x} y={y1} width={bw} height={Math.max(0, y0 - y1 - gap)} fill={CAT_COLOR[c]} fillOpacity={op} />;
              });
            })}
            <text x={cx} y={H - 10} textAnchor="middle" fontSize={11} fill="var(--text-light)">{MONTHS[m]}</text>
            <rect x={L + gw * i} y={T} width={gw} height={ph} fill="transparent" style={{ cursor: 'pointer' }} onClick={() => onMonth(m)}>
              <title>{tipFor(m)}</title>
            </rect>
          </g>
        );
      })}
      <line x1={L} x2={W - R} y1={Y(0)} y2={Y(0)} stroke="var(--border)" />
    </svg>
  );
}

function YoyUtilChart({ series, months, cy, py, capOf, withProductive, onMonth }) {
  const W = 720, H = 260, L = 40, R = 10, T = 12, B = 28;
  const lines = [{ c: 'B', y: cy, dash: false, label: `Billable ${cy}` }, { c: 'B', y: py, dash: true, label: `Billable ${py}` }];
  if (withProductive) lines.push({ c: 'P', y: cy, dash: false, prod: true, label: `Productive ${cy}` }, { c: 'P', y: py, dash: true, prod: true, label: `Productive ${py}` });
  const v = (l, m) => {
    const o = (l.y === cy ? series.cy : series.py)[m];
    return pct(l.prod ? o.B + o.P : o.B, capOf(l.y, m));
  };
  const top = Math.max(100, ...lines.flatMap((l) => months.map((m) => v(l, m) || 0)));
  const pw = W - L - R, ph = H - T - B, gw = pw / months.length;
  const X = (i) => L + gw * i + gw / 2, Y = (x) => T + ph - (x / top) * ph;
  return (
    <>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-mid)', marginBottom: 6 }}>
        {lines.map((l) => (
          <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <svg width="22" height="8" aria-hidden="true"><line x1="0" x2="22" y1="4" y2="4" stroke={CAT_COLOR[l.c]} strokeWidth="2" strokeDasharray={l.dash ? '5 4' : undefined} /></svg>{l.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Utilization by month">
        {[0, 25, 50, 75, 100].map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={Y(t)} y2={Y(t)} stroke="var(--border)" strokeOpacity={0.6} />
            <text x={L - 6} y={Y(t) + 4} textAnchor="end" fontSize={11} fill="var(--text-light)">{t}%</text>
          </g>
        ))}
        {lines.map((l) => {
          const pts = months.map((m, i) => [X(i), v(l, m)]).filter((p) => p[1] != null);
          return (
            <g key={l.label} pointerEvents="none">
              <polyline fill="none" stroke={CAT_COLOR[l.c]} strokeWidth="2" strokeDasharray={l.dash ? '5 4' : undefined} strokeOpacity={l.dash ? 0.75 : 1} points={pts.map((p) => `${p[0]},${Y(p[1])}`).join(' ')} />
              {!l.dash && pts.map((p, i) => <circle key={i} cx={p[0]} cy={Y(p[1])} r={i === pts.length - 1 ? 4.5 : 3} fill={CAT_COLOR[l.c]} stroke="var(--surface)" strokeWidth="2" />)}
            </g>
          );
        })}
        {months.map((m, i) => (
          <g key={m}>
            <text x={X(i)} y={H - 10} textAnchor="middle" fontSize={11} fill="var(--text-light)">{MONTHS[m]}</text>
            <rect x={L + gw * i} y={T} width={gw} height={ph} fill="transparent" style={{ cursor: 'pointer' }} onClick={() => onMonth(m)}>
              <title>{[MONTHS[m], ...lines.map((l) => `${l.label}: ${fmtPct(v(l, m))}`)].join('\n')}</title>
            </rect>
          </g>
        ))}
      </svg>
    </>
  );
}

const YOY_VALUES = { act: 'ACT hours', B: 'Billable hours', P: 'NB productive hours', N: 'Non-billable hours', T: 'Time off hours', bp: 'Billable % of ACT' };
const COMPARE_DIMS = ['team', 'person', 'account', 'project', 'projectType', 'billedTo'];
const PROJECT_TYPE_YOY = [...PROJECT_TYPES, 'Administrative'].map((t) => ({ id: t, name: t }));

function ActYoyTab({ years }) {
  const yearNums = useMemo(() => years.map((y) => 2000 + Number(String(y).slice(2))).filter(Number.isFinite).sort((a, b) => b - a), [years]);
  const [cy, setCy] = useRememberedPeriod('timeReports.yoy.year', null);
  const [py, setPy] = useRememberedPeriod('timeReports.yoy.compareYear', null);
  const [rangeSel, setRangeSel] = useRememberedPeriod('timeReports.yoy.range', null);
  const [teams, setTeams] = usePersistedFilter('timeReports.yoy.teams', []);
  const [people, setPeople] = usePersistedFilter('timeReports.yoy.people', []);
  const [accounts, setAccounts] = usePersistedFilter('timeReports.yoy.accounts', []);
  const [projectIds, setProjectIds] = usePersistedFilter('timeReports.yoy.projects', []);
  const [projectTypes, setProjectTypes] = usePersistedFilter('timeReports.yoy.projectTypes', []);
  const [cats, setCats] = usePersistedFilter('timeReports.yoy.cats', []);
  const [taskTypeIds, setTaskTypeIds] = usePersistedFilter('timeReports.yoy.taskTypes', []);
  const [billedTo, setBilledTo] = usePersistedFilter('timeReports.yoy.billedTo', []);
  const [approvedOnly, setApprovedOnly] = usePersistedFilter('timeReports.yoy.approvedOnly', false);
  const [measure, setMeasure] = useState('h');
  const [hidden, setHidden] = useState([]);
  const [withProductive, setWithProductive] = useState(false);
  const [nbScope, setNbScope] = useState('PNT');
  const [compareDim, setCompareDim] = useState('team');
  const [drill, setDrill] = useState(null);

  // One fetch per year pair; every other filter slices the payload client-side.
  const url = `/dashboard/reports/act-yoy${cy ? `?year=${cy}&compareYear=${py || cy - 1}` : ''}`;
  const { data, loading, error } = useReportFetch(url);
  const norm = useMemo(() => (data ? normalizeYoy(data) : null), [data]);
  const year = data?.year, prior = data?.compareYear;
  const { from, to } = rangeSel || (norm ? defaultRange(norm.months, year) : { from: 0, to: 11 });
  const f = { from, to, teams, people, accounts, projects: projectIds, projectTypes, cats, taskTypes: taskTypeIds, billedTo, approvedOnly };
  const fKey = JSON.stringify(f);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = useMemo(() => (norm ? filterRows(norm, f) : []), [norm, fKey]);
  const months = yoyRange(from, to);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scopeIds = useMemo(() => (norm ? scopePeople(norm, f) : []), [norm, teams, people]);
  const capOf = useCallback((y, m) => (norm ? yoyCapacity(norm, y, [m], scopeIds) : 0), [norm, scopeIds]);
  const series = useMemo(() => monthSeries(rows, year, prior), [rows, year, prior]);
  const sums = useMemo(() => sumRows(rows, year, prior), [rows, year, prior]);
  const D = useMemo(() => (norm ? dims(norm) : null), [norm]);

  const opts = useMemo(() => {
    if (!norm) return null;
    const byName = (a, b) => a.name.localeCompare(b.name);
    const accts = [...new Set(norm.rows.map((r) => r.a))].sort().map((a) => ({ id: a, name: a }));
    const typeCats = new Set(norm.rows.map((r) => `${r.t}|${r.c}`));
    return {
      teams: [...norm.teams.values()].sort(byName),
      people: [...norm.users.values()].filter((u) => !teams.length || teams.includes(u.teamId)).sort(byName),
      accounts: accts,
      projects: [...norm.projects.values()].filter((p) => !accounts.length || accounts.includes(p.accountName)).sort(byName),
      taskTypes: [...norm.taskTypes.values()].filter((t) => !cats.length || cats.some((c) => typeCats.has(`${t.id}|${c}`))).sort(byName),
    };
  }, [norm, teams, accounts, cats]);

  function resetFilters() {
    [setTeams, setPeople, setAccounts, setProjectIds, setProjectTypes, setCats, setTaskTypeIds, setBilledTo].forEach((s) => s([]));
    setApprovedOnly(false);
    setRangeSel(null);
  }
  const openDrill = (crumbs, group) => setDrill({ crumbs, group });

  function exportExcel() {
    const sheetRows = rows.filter((r) => r.y === year || r.y === prior).map((r) => ({
      Year: r.y, Month: MONTHS[r.m], Team: D.team.name(r.team), Person: D.person.name(r.p), Account: r.a,
      Project: D.project.name(r.pr), 'Project Type': D.projectType.key(r), 'Task Type': D.taskType.name(r.t),
      Category: CAT_LABEL[r.c], 'Billed To': D.billedTo.name(D.billedTo.key(r)), Approved: r.ok ? 'Yes' : 'No', Hours: r.h,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetRows), 'ACT YoY');
    XLSX.writeFile(wb, `ACT Year over Year ${year} vs ${prior} ${MONTHS[from]}-${MONTHS[to]}.xlsx`);
  }

  if (error) return <ErrorBanner error={error} />;
  if (!norm || loading) return <div className="card" style={{ color: 'var(--text-light)' }}>Loading two years of hours… this can take several seconds.</div>;

  const scopeCapCy = yoyCapacity(norm, year, months, scopeIds), scopeCapPy = yoyCapacity(norm, prior, months, scopeIds);
  const openMonth = norm.months.find((m) => m.year === year && !m.closed && m.month - 1 >= from && m.month - 1 <= to);
  const tiles = [
    { label: 'ACT hours', c: yoyTotal(sums.cy), p: yoyTotal(sums.py), pred: null, group: 'team' },
    { label: 'Billable', cat: 'B', c: sums.cy.B, p: sums.py.B, group: 'team' },
    { label: 'Utilization', c: pct(sums.cy.B, scopeCapCy), p: pct(sums.py.B, scopeCapPy), isPct: true, cat: 'B', group: 'team' },
    { label: 'Productive util.', c: pct(sums.cy.B + sums.cy.P, scopeCapCy), p: pct(sums.py.B + sums.py.P, scopeCapPy), isPct: true, group: 'team', cats: ['B', 'P'] },
    { label: 'NB productive', cat: 'P', c: sums.cy.P, p: sums.py.P, group: 'taskType' },
    { label: 'Non-billable', cat: 'N', c: sums.cy.N, p: sums.py.N, group: 'taskType' },
    { label: 'Time off', cat: 'T', c: sums.cy.T, p: sums.py.T, group: 'person' },
  ];

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
            <label className="form-label">Year</label>
            <select className="form-select" value={year} onChange={(e) => { const y = Number(e.target.value); setCy(y); setPy(y === prior ? y - 1 : prior); setRangeSel(null); }}>
              {yearNums.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, width: 110 }}>
            <label className="form-label">Compare to</label>
            <select className="form-select" value={prior} onChange={(e) => { setCy(year); setPy(Number(e.target.value)); }}>
              {yearNums.filter((y) => y !== year).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, width: 90 }}>
            <label className="form-label">From</label>
            <select className="form-select" value={from} onChange={(e) => { const v = Number(e.target.value); setRangeSel({ from: v, to: Math.max(v, to) }); }}>
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, width: 90 }}>
            <label className="form-label">To</label>
            <select className="form-select" value={to} onChange={(e) => { const v = Number(e.target.value); setRangeSel({ from: Math.min(from, v), to: v }); }}>
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </div>
          <OptionMultiSelect label="Team" options={opts.teams} selected={teams} onChange={(v) => { setTeams(v); setPeople([]); }} allLabel="All teams" noun="teams" />
          <OptionMultiSelect label="Person" options={opts.people} selected={people} onChange={setPeople} allLabel="All people" noun="people" />
          <OptionMultiSelect label="Account" options={opts.accounts} selected={accounts} onChange={(v) => { setAccounts(v); setProjectIds([]); }} allLabel="All accounts" noun="accounts" />
          <OptionMultiSelect label="Project" options={opts.projects} selected={projectIds} onChange={setProjectIds} allLabel="All projects" noun="projects" width={180} />
          <OptionMultiSelect label="Project type" options={PROJECT_TYPE_YOY} selected={projectTypes} onChange={setProjectTypes} allLabel="All types" noun="types" />
          <OptionMultiSelect label="Category" options={CATS.map((c) => ({ id: c, name: CAT_LABEL[c] }))} selected={cats} onChange={(v) => { setCats(v); setTaskTypeIds([]); }} allLabel="All categories" noun="categories" />
          <OptionMultiSelect label="Task type" options={opts.taskTypes} selected={taskTypeIds} onChange={setTaskTypeIds} allLabel="All task types" noun="task types" width={170} />
          <OptionMultiSelect label="Billed to" options={Object.entries(BILLED_LABEL).map(([id, name]) => ({ id, name }))} selected={billedTo} onChange={setBilledTo} allLabel="All" noun="selected" width={130} />
          <div className="form-group" style={{ marginBottom: 0, width: 150 }}>
            <label className="form-label">Approval</label>
            <select className="form-select" value={approvedOnly ? 'approved' : 'all'} onChange={(e) => setApprovedOnly(e.target.value === 'approved')}>
              <option value="all">All logged time</option>
              <option value="approved">Approved only</option>
            </select>
          </div>
          <button type="button" className="btn btn-outline" onClick={resetFilters}>Reset</button>
          <button type="button" className="btn btn-outline" onClick={exportExcel}>Export to Excel</button>
        </div>
        {openMonth && (
          <div className="notice notice-warn" style={{ marginTop: 12, marginBottom: 0 }}>
            {MONTHS[openMonth.month - 1]} {year} is still open, so its hours and utilization are partial.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {tiles.map((t) => (
          <button
            key={t.label} type="button" className="card"
            style={{ flex: '1 1 150px', minWidth: 150, padding: '12px 16px', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
            onClick={() => openDrill([{ label: t.label, cats: t.cats || (t.cat ? [t.cat] : null) }], t.group)}
          >
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-light)' }}>{t.cat && <Swatch c={t.cat} />}{t.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--heading)', fontVariantNumeric: 'tabular-nums' }}>{t.isPct ? fmtPct(t.c) : fmtH(t.c)}</div>
            <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>
              {t.isPct ? <DeltaPts c={t.c} p={t.p} /> : deltaPct(t.c, t.p)} <span style={{ color: 'var(--text-light)' }}>vs {t.isPct ? fmtPct(t.p) : fmtH(t.p)} in {prior}</span>
            </div>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16, marginBottom: 16 }}>
        <div className="card" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <div>
              <div className="card-title">ACT by month</div>
              <div style={{ fontSize: 12, color: 'var(--text-light)' }}>Left bar {prior} (faded), right bar {year}. Click a month to break it down.</div>
            </div>
            <select className="form-select" style={{ width: 150 }} value={measure} onChange={(e) => setMeasure(e.target.value)} aria-label="Measure">
              <option value="h">Hours</option>
              <option value="pct">% of capacity</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, marginBottom: 6 }}>
            {CATS.map((c) => (
              <button key={c} type="button" onClick={() => setHidden((h) => (h.includes(c) ? h.filter((x) => x !== c) : [...h, c]))} aria-pressed={!hidden.includes(c)}
                style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--text-mid)', opacity: hidden.includes(c) ? 0.4 : 1, textDecoration: hidden.includes(c) ? 'line-through' : 'none' }}>
                <Swatch c={c} />{CAT_LABEL[c]}
              </button>
            ))}
          </div>
          <YoyMonthChart series={series} months={months} cy={year} py={prior} hidden={hidden} measure={measure} capOf={capOf} onMonth={(m) => openDrill([{ label: MONTHS[m], month: m }], 'team')} />
        </div>
        <div className="card" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <div>
              <div className="card-title">Utilization by month</div>
              <div style={{ fontSize: 12, color: 'var(--text-light)' }}>Billable ÷ capacity. Dashed is {prior}.</div>
            </div>
            <label style={{ fontSize: 12, color: 'var(--text-mid)' }}>
              <input type="checkbox" checked={withProductive} onChange={(e) => setWithProductive(e.target.checked)} /> Add productive
            </label>
          </div>
          <YoyUtilChart series={series} months={months} cy={year} py={prior} capOf={capOf} withProductive={withProductive}
            onMonth={(m) => openDrill([{ label: MONTHS[m], month: m }, { label: 'Billable', cats: ['B'] }], 'team')} />
        </div>
      </div>

      <YoyNonBillable norm={norm} rows={rows} year={year} prior={prior} scope={nbScope} setScope={setNbScope} total={yoyTotal(sums.cy)} onOpen={openDrill} />
      <YoyCompare norm={norm} D={D} rows={rows} f={f} year={year} prior={prior} months={months} dim={compareDim} setDim={setCompareDim} onOpen={openDrill} />
      <YoyDetail norm={norm} D={D} rows={rows} f={f} year={year} prior={prior} months={months} scopeIds={scopeIds} onOpen={openDrill} />
      {drill && <YoyDrill norm={norm} D={D} rows={rows} year={year} prior={prior} months={months} capOf={capOf} drill={drill} setDrill={setDrill} />}
    </>
  );
}

function YoyNonBillable({ norm, rows, year, prior, scope, setScope, total, onOpen }) {
  const [sort, onSort] = useSort({ key: 'cy', dir: 'desc' });
  const data = useMemo(() => {
    const g = yoyGroupBy(rows.filter((r) => scope.includes(r.c)), (r) => `${r.t}|${r.c}`);
    return [...g].map(([key, rs]) => {
      const [t, c] = key.split('|');
      const s = sumRows(rs, year, prior);
      const tt = norm.taskTypes.get(t);
      const cyH = s.cy[c], pyH = s.py[c];
      return { key, t, c, name: tt?.name || 'Unknown', cat: CAT_LABEL[c], billed: BILLED_LABEL[tt?.activityBilledTo] || '—', cy: cyH, py: pyH, d: cyH - pyH, dp: pyH ? ((cyH - pyH) / pyH) * 100 : null, share: pct(cyH, total) };
    }).filter((d) => d.cy || d.py);
  }, [norm, rows, year, prior, scope, total]);
  const sorted = sortBy(data, sort, (r, k) => r[k]);
  const mx = Math.max(1, ...data.map((d) => Math.max(d.cy, d.py)));
  const tc = data.reduce((a, d) => a + d.cy, 0), tp = data.reduce((a, d) => a + d.py, 0);
  const scopes = [['PNT', 'All non-billable'], ['P', 'NB productive'], ['N', 'Non-billable'], ['T', 'Time off']];
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div className="card-title">Non-billable time by task type</div>
          <div style={{ fontSize: 12, color: 'var(--text-light)' }}>Every non-billable task type, {year} against {prior}. Click a row to see who logged it.</div>
        </div>
        <select className="form-select" style={{ width: 170 }} value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Category">
          {scopes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="sheet-wrap">
        <table className="sheet">
          <thead>
            <tr>
              <SortTh label="Task type" sortKey="name" sort={sort} onSort={onSort} />
              <SortTh label="Category" sortKey="cat" sort={sort} onSort={onSort} />
              <SortTh label="Billed to" sortKey="billed" sort={sort} onSort={onSort} />
              <th>{year} vs {prior}</th>
              <SortTh label={String(year)} sortKey="cy" sort={sort} onSort={onSort} style={THR} />
              <SortTh label={String(prior)} sortKey="py" sort={sort} onSort={onSort} style={THR} />
              <SortTh label="Change" sortKey="d" sort={sort} onSort={onSort} style={THR} />
              <SortTh label="Change %" sortKey="dp" sort={sort} onSort={onSort} style={THR} />
              <SortTh label="% of ACT" sortKey="share" sort={sort} onSort={onSort} style={THR} title="Share of all ACT hours in the current filters" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr key={d.key} style={{ cursor: 'pointer' }} onClick={() => onOpen([{ label: d.name, dim: 'taskType', k: d.t }, { label: d.cat, cats: [d.c] }], 'team')}>
                <td style={TDL}>{d.name}</td>
                <td style={TDL}><Swatch c={d.c} />{d.cat}</td>
                <td style={TDL}>{d.billed}</td>
                <td style={{ ...TDL, minWidth: 160 }}>
                  <div style={{ height: 9, width: `${(d.cy / mx) * 100}%`, background: CAT_COLOR[d.c], borderRadius: '0 3px 3px 0' }} />
                  <div style={{ height: 6, marginTop: 2, width: `${(d.py / mx) * 100}%`, background: CAT_COLOR[d.c], opacity: 0.4, borderRadius: '0 3px 3px 0' }} />
                </td>
                <td style={TDR}>{fmtH(d.cy)}</td>
                <td style={TDR}>{fmtH(d.py)}</td>
                <td style={TDR}>{signed(d.d)}</td>
                <td style={TDR}>{deltaPct(d.cy, d.py)}</td>
                <td style={TDR}>{fmtPct(d.share)}</td>
              </tr>
            ))}
            <tr className="is-group">
              <td>Total</td><td /><td /><td />
              <td style={TDR}>{fmtH(tc)}</td><td style={TDR}>{fmtH(tp)}</td><td style={TDR}>{signed(tc - tp)}</td><td style={TDR}>{deltaPct(tc, tp)}</td><td style={TDR}>{fmtPct(pct(tc, total))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function YoyCompare({ norm, D, rows, f, year, prior, months, dim, setDim, onOpen }) {
  const [sort, onSort] = useSort({ key: 'util', dir: 'desc' });
  const hasCap = dim === 'team' || dim === 'person';
  const data = useMemo(() => {
    const scope = scopePeople(norm, f);
    return [...yoyGroupBy(rows, D[dim].key)].map(([k, rs]) => {
      const s = sumRows(rs, year, prior);
      const ids = hasCap ? scope.filter((id) => (dim === 'team' ? norm.users.get(id)?.teamId === k : id === k)) : [];
      const cyT = yoyTotal(s.cy), pyT = yoyTotal(s.py);
      const util = hasCap ? pct(s.cy.B, yoyCapacity(norm, year, months, ids)) : null;
      const putil = hasCap ? pct(s.py.B, yoyCapacity(norm, prior, months, ids)) : null;
      return { k, name: D[dim].name(k), s, cy: cyT, py: pyT, dp: pyT ? ((cyT - pyT) / pyT) * 100 : null, bp: pct(s.cy.B, cyT), nb: s.cy.P + s.cy.N, util, putil, du: util != null && putil != null ? util - putil : null };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [norm, D, rows, dim, year, prior, months.join(), hasCap]);
  const sorted = sortBy(data, sort, (r, k) => r[k]);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div className="card-title">Mix and utilization by {D[dim].label.toLowerCase()}</div>
          <div style={{ fontSize: 12, color: 'var(--text-light)' }}>Thick bar is {year}, thin bar {prior}. Utilization needs capacity, so it shows for Team and Person only.</div>
        </div>
        <div className="form-group" style={{ marginBottom: 0, width: 160 }}>
          <label className="form-label">Compare by</label>
          <select className="form-select" value={dim} onChange={(e) => setDim(e.target.value)}>
            {COMPARE_DIMS.map((k) => <option key={k} value={k}>{D[k].label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-mid)', marginBottom: 8 }}>
        {CATS.map((c) => <span key={c}><Swatch c={c} />{CAT_LABEL[c]}</span>)}
      </div>
      <div className="sheet-wrap">
        <table className="sheet">
          <thead>
            <tr>
              <SortTh label={D[dim].label} sortKey="name" sort={sort} onSort={onSort} />
              <th>Mix</th>
              <SortTh label={`ACT ${year}`} sortKey="cy" sort={sort} onSort={onSort} style={THR} />
              <SortTh label={`ACT ${prior}`} sortKey="py" sort={sort} onSort={onSort} style={THR} />
              <SortTh label="Change %" sortKey="dp" sort={sort} onSort={onSort} style={THR} />
              <SortTh label="Billable %" sortKey="bp" sort={sort} onSort={onSort} style={THR} />
              <SortTh label="NB + NB prod." sortKey="nb" sort={sort} onSort={onSort} style={THR} />
              {hasCap && <SortTh label={`Util. ${year}`} sortKey="util" sort={sort} onSort={onSort} style={THR} />}
              {hasCap && <SortTh label={`Util. ${prior}`} sortKey="putil" sort={sort} onSort={onSort} style={THR} />}
              {hasCap && <SortTh label="Util. change" sortKey="du" sort={sort} onSort={onSort} style={THR} />}
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr key={d.k} style={{ cursor: 'pointer' }} onClick={() => onOpen([{ label: d.name, dim, k: d.k }], NEXT_DIM[dim])}>
                <td style={TDL}>{d.name}</td>
                <td style={TDL}><MixBar o={d.s.cy} /><MixBar o={d.s.py} faded /></td>
                <td style={TDR}>{fmtH(d.cy)}</td>
                <td style={TDR}>{fmtH(d.py)}</td>
                <td style={TDR}>{deltaPct(d.cy, d.py)}</td>
                <td style={TDR}>{fmtPct(d.bp)}</td>
                <td style={TDR}>{fmtH(d.nb)}</td>
                {hasCap && <td style={TDR}>{fmtPct(d.util)}</td>}
                {hasCap && <td style={TDR}>{fmtPct(d.putil)}</td>}
                {hasCap && <td style={TDR}><DeltaPts c={d.util} p={d.putil} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function YoyDetail({ norm, D, rows, f, year, prior, months, scopeIds, onOpen }) {
  const [levels, setLevels] = usePersistedFilter('timeReports.yoy.levels', ['team', 'person', 'taskType']);
  const [value, setValue] = useState('act');
  const [showPy, setShowPy] = useState(true);
  const [heat, setHeat] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [sort, onSort] = useSort({ key: 'cyT', dir: 'desc' });
  const lv = levels.filter(Boolean);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tree = useMemo(() => buildTree(norm, rows, lv, f, year, prior), [norm, rows, lv.join(), year, prior]);
  const isPct = value === 'bp';
  const pv = (o) => (value === 'act' ? yoyTotal(o) : isPct ? pct(o.B, yoyTotal(o)) : o[value]);
  const fmt = isPct ? fmtPct : fmtH;
  const get = (n, k) => {
    if (k === 'name') return n.name;
    if (k[0] === 'm') return pv(n.cyM[Number(k.slice(1))]);
    if (k === 'cyT') return pv(n.cyT);
    if (k === 'pyT') return pv(n.pyT);
    if (k === 'd') return (pv(n.cyT) || 0) - (pv(n.pyT) || 0);
    if (k === 'dp') { const a = pv(n.cyT), b = pv(n.pyT); return b ? ((a - b) / b) * 100 : null; }
    return n.util;
  };
  const q = search.trim().toLowerCase();
  const matches = (n) => !q || n.name.toLowerCase().includes(q) || n.children.some(matches);
  let mx = 1;
  const scan = (ns) => ns.forEach((n) => { months.forEach((m) => { mx = Math.max(mx, pv(n.cyM[m]) || 0); }); if (open.has(n.id)) scan(n.children); });
  scan(tree);
  const heatBg = (v) => (heat && v ? `color-mix(in srgb, var(--cat-billable) ${Math.round(Math.min(1, v / (isPct ? 100 : mx)) * 45)}%, transparent)` : undefined);
  const cellTitle = (n, m) => `${n.name} · ${MONTHS[m]}\n${CATS.map((c) => `${CAT_LABEL[c]}: ${fmtH(n.cyM[m][c])} / ${fmtH(n.pyM[m][c])}`).join('\n')}`;

  function drillNode(n, m) {
    const crumbs = n.path.map((x) => ({ label: D[x.dim].name(x.k), dim: x.dim, k: x.k }));
    if (m != null) crumbs.push({ label: MONTHS[m], month: m });
    const used = new Set(n.path.map((x) => x.dim));
    let g = NEXT_DIM[n.dim];
    if (used.has(g)) g = ['team', 'person', 'project', 'taskType', 'account'].find((d) => !used.has(d)) || 'month';
    onOpen(crumbs, g);
  }
  const out = [];
  const walk = (ns, depth) => {
    for (const n of sortBy(ns.filter(matches), sort, get)) {
      const expanded = open.has(n.id) || (q && n.children.some(matches));
      const a = pv(n.cyT), b = pv(n.pyT);
      out.push(
        <tr key={n.id} style={depth === 0 ? { fontWeight: 600 } : undefined}>
          <td className="is-frozen" style={{ ...TDL, left: 0, paddingLeft: 10 + depth * 18, cursor: 'pointer', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={n.name}
            onClick={() => { if (!n.children.length) { drillNode(n, null); return; } setOpen((s) => { const x = new Set(s); if (x.has(n.id)) x.delete(n.id); else x.add(n.id); return x; }); }}>
            <span style={{ display: 'inline-block', width: 14, color: 'var(--text-light)' }}>{n.children.length ? (expanded ? '▾' : '▸') : ''}</span>{n.name}
          </td>
          {months.map((m) => (
            <td key={m} style={{ ...TDR, cursor: 'pointer', background: heatBg(pv(n.cyM[m])) }} title={cellTitle(n, m)} onClick={() => drillNode(n, m)}>
              {fmt(pv(n.cyM[m]))}{showPy && <span style={PY_SUB}>{fmt(pv(n.pyM[m]))}</span>}
            </td>
          ))}
          <td style={{ ...TDR, cursor: 'pointer', fontWeight: 600 }} onClick={() => drillNode(n, null)}>{fmt(a)}</td>
          <td style={TDR}>{fmt(b)}</td>
          <td style={TDR}>{isPct ? <DeltaPts c={a} p={b} /> : signed((a || 0) - (b || 0))}</td>
          <td style={TDR}>{isPct ? '—' : deltaPct(a, b)}</td>
          <td style={TDR}>{fmtPct(n.util)}</td>
        </tr>,
      );
      if (expanded && n.children.length) walk(n.children, depth + 1);
    }
  };
  walk(tree, 0);
  const all = sumRows(rows, year, prior);
  const tm = monthSeries(rows, year, prior);
  const expandAll = () => { const s = new Set(); const add = (ns) => ns.forEach((n) => { s.add(n.id); add(n.children); }); add(tree); setOpen(s); };
  const levelOpts = (i) => Object.keys(D).filter((k) => k !== 'month' && !lv.slice(0, i).includes(k));
  const setLevel = (i, v) => { const next = [...levels]; next[i] = v || null; setLevels(next.slice(0, v ? 3 : i).filter((x, j, arr) => x && arr.indexOf(x) === j)); setOpen(new Set()); };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title">ACT detail by month</div>
      <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 10 }}>Pick up to three row levels. Click a name to expand it, or a number to see what makes it up. Gray figures underneath are {prior}.</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
        {[0, 1, 2].map((i) => (i === 0 || lv[i - 1]) && (
          <div key={i} className="form-group" style={{ marginBottom: 0, width: 140 }}>
            <label className="form-label">{i === 0 ? 'Rows' : 'Then'}</label>
            <select className="form-select" value={lv[i] || ''} onChange={(e) => setLevel(i, e.target.value)}>
              {i > 0 && <option value="">(none)</option>}
              {levelOpts(i).map((k) => <option key={k} value={k}>{D[k].label}</option>)}
            </select>
          </div>
        ))}
        <div className="form-group" style={{ marginBottom: 0, width: 170 }}>
          <label className="form-label">Show</label>
          <select className="form-select" value={value} onChange={(e) => setValue(e.target.value)}>
            {Object.entries(YOY_VALUES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0, width: 180 }}>
          <label className="form-label">Find a row</label>
          <input className="form-input" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name contains…" />
        </div>
        <label style={{ fontSize: 13 }}><input type="checkbox" checked={showPy} onChange={(e) => setShowPy(e.target.checked)} /> Show {prior}</label>
        <label style={{ fontSize: 13 }}><input type="checkbox" checked={heat} onChange={(e) => setHeat(e.target.checked)} /> Heatmap</label>
        <button type="button" className="btn btn-outline" onClick={expandAll}>Expand all</button>
        <button type="button" className="btn btn-outline" onClick={() => setOpen(new Set())}>Collapse all</button>
      </div>
      <div className="sheet-wrap" style={{ maxHeight: 640, overflow: 'auto' }}>
        <table className="sheet">
          <thead>
            <tr>
              <SortTh label={lv.map((k) => D[k].label).join(' › ')} sortKey="name" sort={sort} onSort={onSort} style={{ position: 'sticky', left: 0, zIndex: 4 }} />
              {months.map((m) => <SortTh key={m} label={MONTHS[m]} sortKey={`m${m}`} sort={sort} onSort={onSort} style={THR} />)}
              <SortTh label={`Total ${year}`} sortKey="cyT" sort={sort} onSort={onSort} style={THR} />
              <SortTh label={`Total ${prior}`} sortKey="pyT" sort={sort} onSort={onSort} style={THR} />
              <SortTh label="Change" sortKey="d" sort={sort} onSort={onSort} style={THR} />
              <SortTh label="Change %" sortKey="dp" sort={sort} onSort={onSort} style={THR} />
              <SortTh label={`Util. ${year}`} sortKey="util" sort={sort} onSort={onSort} style={THR} title="Only where every level is Team or Person, since capacity belongs to people" />
            </tr>
          </thead>
          <tbody>
            {out.length ? out : <tr><td colSpan={months.length + 6} style={{ ...TDL, color: 'var(--text-light)' }}>No hours match these filters.</td></tr>}
            {out.length > 0 && (
              <tr className="is-group">
                <td className="is-frozen" style={{ left: 0 }}>Total</td>
                {months.map((m) => <td key={m} style={TDR}>{fmt(pv(tm.cy[m]))}{showPy && <span style={PY_SUB}>{fmt(pv(tm.py[m]))}</span>}</td>)}
                <td style={TDR}>{fmt(pv(all.cy))}</td>
                <td style={TDR}>{fmt(pv(all.py))}</td>
                <td style={TDR}>{isPct ? <DeltaPts c={pv(all.cy)} p={pv(all.py)} /> : signed(pv(all.cy) - pv(all.py))}</td>
                <td style={TDR}>{isPct ? '—' : deltaPct(pv(all.cy), pv(all.py))}</td>
                <td style={TDR}>{fmtPct(pct(all.cy.B, yoyCapacity(norm, year, months, scopeIds)))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Side panel: every drill-down on the tab lands here. Each crumb narrows the rows further; clicking
// a breakdown row adds one more crumb.
function YoyDrill({ norm, D, rows, year, prior, months, capOf, drill, setDrill }) {
  const [sort, onSort] = useSort({ key: 'cy', dir: 'desc' });
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setDrill(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setDrill]);
  const match = (r) => drill.crumbs.every((c) => (c.month != null ? r.m === c.month : c.dim ? D[c.dim].key(r) === c.k : c.cats ? c.cats.includes(r.c) : true));
  const scoped = rows.filter(match);
  const s = sumRows(scoped, year, prior);
  const series = monthSeries(scoped, year, prior);
  const used = new Set(drill.crumbs.map((c) => (c.month != null ? 'month' : c.dim)).filter(Boolean));
  const group = drill.group;
  const data = [...yoyGroupBy(scoped, D[group].key)].map(([k, rs]) => {
    const x = sumRows(rs, year, prior);
    const cyT = yoyTotal(x.cy), pyT = yoyTotal(x.py);
    return { k, name: D[group].name(k), x, cy: cyT, py: pyT, d: cyT - pyT, bp: pct(x.cy.B, cyT), share: pct(cyT, yoyTotal(s.cy)) };
  });
  const sorted = sortBy(data, sort, (r, k) => r[k]);
  const pending = scoped.filter((r) => r.y === year && !r.ok).reduce((a, r) => a + r.h, 0);
  const push = (d) => {
    const crumbs = [...drill.crumbs, group === 'month' ? { label: d.name, month: d.k } : { label: d.name, dim: group, k: d.k }];
    const taken = new Set([...used, group]);
    let next = NEXT_DIM[group];
    if (taken.has(next)) next = ['team', 'person', 'project', 'taskType', 'account', 'month'].find((x) => !taken.has(x)) || 'month';
    setDrill({ crumbs, group: next });
  };
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000 }} onClick={() => setDrill(null)} />
      <aside role="dialog" aria-modal="true" aria-label="Drill-down"
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(640px, 100%)', background: 'var(--surface)', zIndex: 1001, boxShadow: '-8px 0 30px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, fontSize: 13, alignItems: 'center' }}>
              {drill.crumbs.map((c, i) => (i < drill.crumbs.length - 1 ? (
                <React.Fragment key={i}>
                  <button type="button" className="btn-link" style={{ background: 'none', border: 0, padding: 0, color: 'var(--primary-light)', cursor: 'pointer' }}
                    onClick={() => setDrill({ crumbs: drill.crumbs.slice(0, i + 1), group: drill.group })}>{c.label}</button>
                  <span style={{ color: 'var(--text-light)' }}>›</span>
                </React.Fragment>
              ) : <strong key={i}>{c.label}</strong>))}
            </div>
            <button type="button" className="btn btn-outline" onClick={() => setDrill(null)} autoFocus>Close</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>{year} vs {prior} · {MONTHS[months[0]]}–{MONTHS[months[months.length - 1]]} · page filters still apply</div>
        </div>
        <div style={{ padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
            {[[`ACT ${year}`, fmtH(yoyTotal(s.cy))], [`ACT ${prior}`, fmtH(yoyTotal(s.py))], ['Change', deltaPct(yoyTotal(s.cy), yoyTotal(s.py))], ['Billable %', fmtPct(pct(s.cy.B, yoyTotal(s.cy)))]].map(([l, v]) => (
              <div key={l} style={{ background: 'var(--light-bg)', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-light)' }}>{l}</div>
                <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
              </div>
            ))}
          </div>
          <YoyMonthChart series={series} months={months} cy={year} py={prior} hidden={[]} measure="h" capOf={capOf} height={190}
            onMonth={(m) => { if (!used.has('month')) setDrill({ crumbs: [...drill.crumbs, { label: MONTHS[m], month: m }], group: group === 'month' ? 'team' : group }); }} />
          {pending > 0 && <div className="notice notice-warn" style={{ marginBottom: 0 }}>{fmtH(pending)} of the {year} hours here are still waiting for approval.</div>}
          <div className="form-group" style={{ marginBottom: 0, width: 180 }}>
            <label className="form-label">Break down by</label>
            <select className="form-select" value={group} onChange={(e) => setDrill({ ...drill, group: e.target.value })}>
              {Object.keys(D).filter((k) => k === group || !used.has(k)).map((k) => <option key={k} value={k}>{D[k].label}</option>)}
            </select>
          </div>
          <div className="sheet-wrap">
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label={D[group].label} sortKey="name" sort={sort} onSort={onSort} />
                  <th>Mix</th>
                  <SortTh label={String(year)} sortKey="cy" sort={sort} onSort={onSort} style={THR} />
                  <SortTh label={String(prior)} sortKey="py" sort={sort} onSort={onSort} style={THR} />
                  <SortTh label="Change" sortKey="d" sort={sort} onSort={onSort} style={THR} />
                  <SortTh label="Billable %" sortKey="bp" sort={sort} onSort={onSort} style={THR} />
                  <SortTh label="Share" sortKey="share" sort={sort} onSort={onSort} style={THR} />
                </tr>
              </thead>
              <tbody>
                {sorted.map((d) => (
                  <tr key={String(d.k)} style={{ cursor: 'pointer' }} onClick={() => push(d)} title="Click to drill further">
                    <td style={TDL}>{d.name}</td>
                    <td style={TDL}><MixBar o={d.x.cy} /><MixBar o={d.x.py} faded /></td>
                    <td style={TDR}>{fmtH(d.cy)}</td>
                    <td style={TDR}>{fmtH(d.py)}</td>
                    <td style={TDR}>{signed(d.d)}</td>
                    <td style={TDR}>{fmtPct(d.bp)}</td>
                    <td style={TDR}>{fmtPct(d.share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </aside>
    </>
  );
}

// ---------- Top level ----------

const TABS = [
  { key: 'compliance', label: 'Compliance' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'hours-summary', label: 'Hours by Category' },
  { key: 'fcst-vs-actuals', label: 'FCST vs Actuals' },
  { key: 'sightline-feed', label: 'Sightline Feed' },
  { key: 'act-yoy', label: 'ACT Year over Year' },
];

// Merged placeholders are dropped -- their hours and forecast have already moved onto the real
// project (same call as ResourcePlanning's forecastableProjects), so picking one would show nothing.
// Archived projects stay: they still have history worth reporting on, unlike a new forecast.
// Internal-account projects are dropped to match what the report itself excludes (Jeremy,
// 2026-08-09) -- see INTERNAL_ACCOUNT_NAME in reports.service.ts.
export function reportableProjects(projects) {
  return projects
    .filter((p) => !p.mergedIntoProjectId && !p.isAdministrative && p.account?.name !== 'Internal')
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
}

function TimeReports() {
  const [tab, setTab] = usePersistedFilter('timeReports.tab', 'compliance');
  const [periods, setPeriods] = useState([]);
  const [periodMonth, setPeriodMonth] = useRememberedPeriod('timeReports.periodMonth', '');
  const [year, setYear] = useRememberedPeriod('timeReports.year', '');
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [error, setError] = useState('');

  const years = useMemo(() => yearsOf(periods), [periods]);
  const periodsForYear = useMemo(() => (year ? periods.filter((p) => yearOf(p) === year) : periods), [periods, year]);

  // If the year changes and the single-select period (Accounting/Compliance) falls outside it,
  // fall back to that year's first period rather than silently querying a period the dropdown no
  // longer shows as selected.
  useEffect(() => {
    if (periodsForYear.length > 0 && !periodsForYear.includes(periodMonth)) setPeriodMonth(periodsForYear[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodsForYear]);

  // Periods list drives the selectors; the current fiscal month (server-computed, see
  // ReportsService.defaultPeriodMonth) is picked by asking one report for its own default rather
  // than duplicating "today -> fiscal month" logic client-side. Teams/users back the filter
  // dropdowns on Compliance and Hours by Category -- fetched once here, not per-tab.
  const load = useCallback(async () => {
    setError('');
    try {
      const [periodList, defaultReport, teamList, userList, projectList, baselineList] = await Promise.all([
        apiFetch('/dashboard/reports/periods'),
        apiFetch('/dashboard/reports/hours-summary'),
        apiFetch('/teams'),
        apiFetch('/users'),
        apiFetch('/projects'),
        apiFetch('/allocation-baselines'),
      ]);
      setPeriods(periodList);
      // Functional form: only fills in the current period/year when there's no persisted filter
      // choice already restored for this session (usePersistedFilter's initial read wins otherwise).
      setPeriodMonth((prev) => prev || defaultReport.periodMonths?.[0] || '');
      setYear((prev) => prev || yearOf(defaultReport.periodMonths?.[0])); // defaults to the current fiscal year (Jeremy, 2026-08-06)
      setTeams(teamList);
      setUsers(userList);
      setProjects(reportableProjects(projectList));
      setBaselines(baselineList);
    } catch (err) {
      setError(err.message);
    }
    // setPeriodMonth/setYear come from usePersistedDailyFilter rather than plain useState, so eslint
    // can't infer they're stable setters the way it does for the rest -- they are, so listing them
    // here doesn't change when `load` gets recreated.
  }, [setPeriodMonth, setYear]);

  useEffect(() => { load(); }, [load]);

  if (!RESOURCES_ENABLED) {
    return (
      <div className="placeholder-page">
        <div className="placeholder-icon">📊</div>
        <div className="placeholder-title">Time Reports</div>
        <div className="placeholder-sub">
          Built, but not switched on in this environment. Set{' '}
          <code>REACT_APP_RESOURCES_ENABLED=true</code> to enable it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Resourcing</div>
        <h1 className="page-title">Time Reports</h1>
        <p className="page-subtitle">Timesheet compliance, the Finance accounting export, hours by billable category, forecast vs actuals, and actual hours year over year.</p>
      </div>

      <ErrorBanner error={error} />

      {/* Every tab carries its own year/period inside its filter row above the grid (Hours by
          Category 2026-08-07, Accounting + Compliance 2026-08-10) -- nothing else belongs up here.
          Not `merge`: each tab renders its own separate cards below, not one panel to attach to. */}
      <TabBar tabs={TABS} active={tab} onSelect={setTab} />

      {tab === 'compliance' ? (
        <ComplianceTab
          periodMonth={periodMonth}
          periods={periodsForYear}
          onPeriodChange={setPeriodMonth}
          years={years}
          year={year}
          onYearChange={setYear}
          teams={teams}
          users={users}
        />
      ) : tab === 'accounting' ? (
        <AccountingTab
          periodMonth={periodMonth}
          periods={periodsForYear}
          onPeriodChange={setPeriodMonth}
          years={years}
          year={year}
          onYearChange={setYear}
        />
      ) : tab === 'hours-summary' ? (
        <HoursSummaryTab
          periods={periodsForYear}
          defaultPeriodMonth={periodMonth}
          teams={teams}
          users={users}
          years={years}
          year={year}
          onYearChange={setYear}
        />
      ) : tab === 'act-yoy' ? (
        <ActYoyTab years={years} />
      ) : tab === 'sightline-feed' ? (
        <SightlineFeedTab
          periods={periodsForYear}
          defaultPeriodMonth={periodMonth}
          projects={projects}
          years={years}
          year={year}
          onYearChange={setYear}
        />
      ) : (
        <FcstVsActualsTab
          periods={periodsForYear}
          defaultPeriodMonth={periodMonth}
          teams={teams}
          users={users}
          projects={projects}
          baselines={baselines}
          years={years}
          year={year}
          onYearChange={setYear}
        />
      )}
    </div>
  );
}

export default TimeReports;
